import { useState, useEffect, useRef, useCallback } from "react";
import { createClient } from '@supabase/supabase-js';
import { DOMAINS } from './domains';
import {
  PRESETS, PRESET_COLORS,
  parseWikiInput, searchArticles, streamFacts, generateStory, generateFlashcards, getArticleImages,
} from './prompts';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

// ── i18n ───────────────────────────────────────────────────────────────────────

const I18N = {
  ru: {
    inputPlaceholder:"Пушкин, Чёрные дыры, ru.wikipedia.org/wiki/…",
    inputHint:"По умолчанию — русская Википедия. Ссылки en и другие тоже работают.",
    generate:"Создать историю", styleLabel:"Стиль истории",
    basedOn:"На основе статьи",
    openWiki:"Источник: Википедия ↗", regenerate:"Перегенерировать",
    copy:"Скопировать текст", copied:"Скопировано!",
    share:"Поделиться", signOut:"Выйти",
    readStory:"Читать историю", writingStory:"Пишем историю...", didYouKnow:"Пока ждёшь",
    storyReady:"История готова", disambigQ:"Какой", disambigQ2:"вы имели в виду?",
    changeQuery:"← Изменить запрос",
    presets:["Научпоп","Просто и понятно","Академический"],
    presetDescs:["Увлекательно, ~5 мин","Просто как для 8-летнего, ~3 мин","Глубоко и точно, ~15 мин"],
    lengthHints:["~1 мин","~3 мин","~5 мин","~10 мин","~15 мин","~20 мин","~30 мин"],
    toneLabels:["","","Научпоп","","","","Академически","Просто"],
    styleLabels:["","","Популярный","","","","Академически","Просто"],
    emailPlaceholder:"your@email.com",
    magicLinkSent:"✉️ Проверь почту — отправили ссылку для входа",
    orGoogle:"или войти через Google",
    liked:"Нравится", disliked:"Не нравится", reactionThanks:"Спасибо за отзыв!",
    storiesIn:"История", of:"из",
    trackProgress:"прочитано", articles:"историй",
    continueTrack:"Продолжить →", startTrack:"Начать →",
    backToDomains:"← Коллекции",
    todayForYou:"Продолжи учиться",
    continueTrackLabel:"Продолжить путешествие",
    browseAllTracks:"Все коллекции →",
    yourOwnStory:"Свой запрос",
    yourOwnStorySub:"Любая статья Википедии",
    twoMoreWaysLabel:"Ещё два способа",
    knowledgeDomainsLabel:"Коллекции знаний",
    knowledgeDomainsSub:"6 коллекций, 48 путешествий",
    searchPlaceholder:"Чёрные дыры, ДНК, Наполеон…",
    onboardingNext:"Далее →",
    onboardingDone:"Начать →",
    onboardingSkip:"Пропустить",
    onboardingStep1Title:"WikiTale",
    onboardingStep1Sub:"Изучай науку, историю и философию — по одной истории в день",
    onboardingStep1Items:[
      {icon:"🧠", title:"Культурный минимум", desc:"То что должен знать каждый образованный человек"},
      {icon:"📚", title:"Путешествия", desc:"Серия историй по теме — читай по одной в день"},
      {icon:"🎯", title:"Реальный прогресс", desc:"XP, уровни и streak — учёба как игра"},
    ],
    onboardingStep2Title:"Что тебе интересно?",
    onboardingStep2Sub:"Выбери одну тему для старта",
    onboardingStep3Title:"Готово!",
    onboardingStep3Sub:"Первое путешествие ждёт тебя",
    saveProgress:"Войди чтобы сохранить прогресс",
  },
  en: {
    inputPlaceholder:"Black holes, DNA, Napoleon, en.wikipedia.org/wiki/…",
    inputHint:"Defaults to English Wikipedia. Links to ru and other languages also work.",
    generate:"Generate story", styleLabel:"Story style",
    basedOn:"Based on article",
    openWiki:"Source: Wikipedia ↗", regenerate:"Regenerate",
    copy:"Copy text", copied:"Copied!",
    share:"Share", signOut:"Sign out",
    readStory:"Read story", writingStory:"Writing your story...", didYouKnow:"While you wait",
    storyReady:"Your story is ready", disambigQ:"Which", disambigQ2:"did you mean?",
    changeQuery:"← Change search",
    presets:["Pop science","Simply put","Academic"],
    presetDescs:["Engaging & curious, ~5 min","Like you're 8, ~3 min","Deep & precise, ~15 min"],
    lengthHints:["~1 min","~3 min","~5 min","~10 min","~15 min","~20 min","~30 min"],
    toneLabels:["","","Pop science","","","","Scholarly","Simple"],
    styleLabels:["","","Popular","","","","Academic","Simple"],
    emailPlaceholder:"your@email.com",
    magicLinkSent:"✉️ Check your inbox — we sent a sign-in link",
    orGoogle:"or sign in with Google",
    liked:"Like", disliked:"Not for me", reactionThanks:"Thanks for the feedback!",
    storiesIn:"Story", of:"of",
    trackProgress:"read", articles:"stories",
    continueTrack:"Continue →", startTrack:"Start →",
    backToDomains:"← Collections",
    todayForYou:"Keep learning",
    continueTrackLabel:"Continue your journey",
    browseAllTracks:"Explore collections →",
    yourOwnStory:"Your own story",
    yourOwnStorySub:"Any Wikipedia article",
    twoMoreWaysLabel:"Two more ways in",
    knowledgeDomainsLabel:"Knowledge collections",
    knowledgeDomainsSub:"6 collections, 48 journeys",
    searchPlaceholder:"Black holes, DNA, Napoleon…",
    onboardingNext:"Next →",
    onboardingDone:"Let's go →",
    onboardingSkip:"Skip",
    onboardingStep1Title:"WikiTale",
    onboardingStep1Sub:"Learn science, history and philosophy — one story a day",
    onboardingStep1Items:[
      {icon:"🧠", title:"Essential knowledge", desc:"What every educated person should know"},
      {icon:"📚", title:"Journeys", desc:"A story series on a topic — one per day"},
      {icon:"🎯", title:"Real progress", desc:"XP, levels and streaks — learning like a game"},
    ],
    onboardingStep2Title:"What interests you?",
    onboardingStep2Sub:"Pick one topic to start",
    onboardingStep3Title:"You're all set!",
    onboardingStep3Sub:"Your first journey is waiting",
    saveProgress:"Sign in to save your progress",
  },
};

const C = {
  bg:"#F7F5F2", surface:"#EDE9E3", surface2:"#D9D3C7", border:"#C8C2B4",
  accent:"#5C7A6E", accentDim:"#7a9e92", text:"#2a2620", text2:"#6a6050",
  text3:"#b0a898", btnBg:"#5C7A6E", btnText:"#F7F5F2", ornament:"#C4774A",
  redBg:"#fdf0e8", redBorder:"#e8c8a8", red:"#a03010", inputBg:"#FDFCFA",
};

// ── Dark neural tokens (reading/flashcards/victory) ──────────────────
const D = {
  bg:"#06080D", panel:"#0B0E16", panel2:"#12161f", panel3:"#14181f",
  ember:"#C4774A", emberBright:"#E8924E",
  h:"#E8DCC8", body:"#D8DCE4", sec:"#8A90A0", sec2:"#7A8090", ter:"#5A6070", ter2:"#4A5060",
  border:"rgba(255,255,255,.08)", borderSoft:"rgba(255,255,255,.06)",
  read:"#5C7A6E", cur:"#C4774A", front:"#E8924E", lock:"#3A4050",
  serif:"'Newsreader',Georgia,serif", ui:"'Hanken Grotesk',system-ui,sans-serif",
  green:"#5C7A6E", greenBg:"rgba(92,122,110,.18)",
  red:"#9B4A4A", redBg:"rgba(155,74,74,.18)",
};

// ── Pick daily cards ───────────────────────────────────────────────────────────

function getDailyCards(interests, lang) {
  const pool = [];
  DOMAINS.forEach(domain => {
    const interested = interests.length === 0 || interests.includes(domain.id);
    if (!interested) return;
    domain.tracks.forEach(track => {
      track.articles.forEach((article, idx) => {
        pool.push({ domain, track, articleIndex: idx, article });
      });
    });
  });
  if (pool.length === 0) return getDailyCards([], lang);

  const today = new Date();
  const seed = today.getFullYear() * 10000 + (today.getMonth()+1) * 100 + today.getDate();
  const shuffled = [...pool].sort((a, b) => {
    const ha = Math.sin(seed + pool.indexOf(a)) * 10000;
    const hb = Math.sin(seed + pool.indexOf(b)) * 10000;
    return (ha - Math.floor(ha)) - (hb - Math.floor(hb));
  });

  const seen = new Set();
  const cards = [];
  for (const item of shuffled) {
    if (cards.length >= 4) break;
    if (!seen.has(item.domain.id)) {
      seen.add(item.domain.id);
      cards.push(item);
    }
  }
  for (const item of shuffled) {
    if (cards.length >= 4) break;
    if (!cards.includes(item)) cards.push(item);
  }
  return cards;
}

// Split a story into pages of ~2 paragraphs each. Returns array of page strings.
// Splits a story into pages of ~2 paragraphs each. Parses [[IMG:n]] markers left by
// generateStory and attaches the matching image to whichever page its paragraph lands on.
// Returns [{ text, image }] — image is null when no marker applies to that page.
// Extracts a leading [[IMG:n]] marker from any paragraph inside a text block,
// strips it, returns the cleaned text plus the first image found (if any).
// Shared by both the new marker-based pagination and the old mechanical one.
function extractPageImage(text, images) {
  const paras = text.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
  let imgIdx = null;
  const cleaned = paras.map(p => {
    const m = p.match(/^\[\[IMG:(\d+)\]\]\s*/);
    if (m) {
      const idx = +m[1];
      if (imgIdx === null && images[idx]) imgIdx = idx;
      return p.replace(m[0], "");
    }
    return p;
  });
  return { text: cleaned.join("\n\n"), image: imgIdx !== null ? images[imgIdx] : null };
}

// B2: attaches the chapter's pull-quote to whichever page actually contains
// that sentence (exact substring match, since the model was asked to copy it
// verbatim from its own text). Falls back to page 0 if no page matches —
// typography/whitespace drift between the raw quote and the paginated text
// is the likely failure mode, and showing it somewhere beats losing it.
function attachPullQuote(pages, pullQuote) {
  if (!pullQuote || !pages.length) return pages;
  const needle = pullQuote.trim();
  const matchIdx = pages.findIndex(p => p.text.includes(needle));
  const targetIdx = matchIdx >= 0 ? matchIdx : 0;
  return pages.map((p, i) => i === targetIdx ? { ...p, pullQuote: needle } : p);
}

// B2 images backfill: attaches Wikipedia images placed by scene index (see
// distributeImagesOverScenes in generate-library.js's backfill_images mode —
// the story was cut on [[BREAK]] there the exact same way it's cut here, so
// page index === scene index, 1:1, no matching heuristics needed). Only ever
// relevant for [[BREAK]]-based pagination — the backfill skips any story
// without scene markers, so sceneImages is always empty for those anyway.
function attachSceneImages(pages, sceneImages) {
  if (!sceneImages || !sceneImages.length) return pages;
  const byScene = new Map(sceneImages.map(img => [img.sceneIndex, img]));
  return pages.map((p, i) => {
    if (p.image) return p; // don't override an on-demand [[IMG:n]] image, if any
    const img = byScene.get(i);
    return img ? { ...p, image: { url: img.url, caption: img.caption } } : p;
  });
}

function paginateStory(story, images = [], pullQuote = null, sceneImages = []) {
  // C7: the model marks its own scene breaks with [[BREAK]] — each segment
  // becomes one page/screen, aligned with the micro-hooks it was asked to
  // write at those exact boundaries (see SCENE_BEATS in the writing engine).
  // This only applies to stories generated after that change; older stories
  // (or a rare generation that ignored the instruction) have no markers and
  // fall through to the mechanical 2-paragraph pagination below unchanged.
  if (/\[\[BREAK\]\]/.test(story)) {
    const segments = story.split(/\[\[BREAK\]\]/).map(s => s.trim()).filter(Boolean);
    if (segments.length > 1) {
      const pages = segments.map(seg => extractPageImage(seg, images));
      return attachPullQuote(attachSceneImages(pages, sceneImages), pullQuote);
    }
  }

  const rawParas = story.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);

  // Extract a leading [[IMG:n]] marker from each paragraph, strip it from the text
  const paras = rawParas.map(p => {
    const m = p.match(/^\[\[IMG:(\d+)\]\]\s*/);
    if (m) {
      const idx = +m[1];
      return { text: p.replace(m[0], ""), imageIdx: images[idx] ? idx : null };
    }
    return { text: p, imageIdx: null };
  });

  const pages = [];
  for (let i = 0; i < paras.length; i += 2) {
    const chunk = paras.slice(i, i + 2);
    const text = chunk.map(c => c.text).join("\n\n");
    const imgIdx = chunk.find(c => c.imageIdx !== null)?.imageIdx;
    pages.push({ text, image: imgIdx !== undefined ? images[imgIdx] : null });
  }
  return attachPullQuote(pages.length ? pages : [{ text: story, image: null }], pullQuote);
}
// ── Tree animation ─────────────────────────────────────────────────────────────

const TREE_SEQ = [
  ["root2","dash",0,380,0],["root2","op",1,80,0],
  ["root1","dash",0,380,120],["root1","op",1,80,120],
  ["root3","dash",0,380,240],["root3","op",1,80,240],
  ["rd2","op",1,180,380],["rd1","op",1,180,480],["rd3","op",1,180,560],
  ["bookL","op",1,260,700],["bookR","op",1,260,820],["spine","op",1,180,920],
  ["trunk","dash",0,580,920],["trunk","op",1,80,920],
  ["br1","dash",0,320,1420],["br1","op",1,80,1420],
  ["br2","dash",0,320,1520],["br2","op",1,80,1520],
  ["br3","dash",0,280,1720],["br3","op",1,80,1720],
  ["br4","dash",0,280,1820],["br4","op",1,80,1820],
  ["l1","op",1,220,1960],["l3","op",1,220,2060],
  ["l2","op",1,180,2160],["l4","op",1,180,2260],
  ["l5","op",1,180,2360],["l6","op",1,180,2460],
  ["l7","op",1,220,2560],["l8","op",1,180,2660],["l9","op",1,180,2760],
];
const TREE_IDS = ["root1","root2","root3","rd1","rd2","rd3","bookL","bookR",
  "spine","trunk","br1","br2","br3","br4","l1","l2","l3","l4","l5","l6","l7","l8","l9"];

function TreeLogo({ animate }) {
  const refs = {};
  TREE_IDS.forEach(id => { refs[id] = useRef(null); });
  const resetAll = () => {
    TREE_IDS.forEach(id => {
      const el = refs[id].current; if (!el) return;
      el.style.transition = "none"; el.style.opacity = "1";
      if (["root1","root2","root3","trunk","br1","br2","br3","br4"].includes(id)) el.style.strokeDashoffset = "0";
    });
  };
  const resetToEmpty = () => {
    TREE_IDS.forEach(id => {
      const el = refs[id].current; if (!el) return;
      el.style.transition = "none"; el.style.opacity = "0";
      if (["root1","root2","root3","trunk","br1","br2","br3","br4"].includes(id))
        el.style.strokeDashoffset = id === "trunk" ? "120" : "60";
    });
  };
  const applyStep = (id, type, val, dur) => {
    const el = refs[id].current; if (!el) return;
    if (type === "dash") { el.style.transition = `stroke-dashoffset ${dur}ms ease`; el.style.strokeDashoffset = val; }
    else { el.style.transition = `opacity ${dur}ms ease`; el.style.opacity = val; }
  };
  const runSeq = (seq) => new Promise(resolve => {
    let maxEnd = 0;
    seq.forEach(([id,type,val,dur,delay]) => { setTimeout(() => applyStep(id,type,val,dur), delay); maxEnd = Math.max(maxEnd, delay + dur); });
    setTimeout(resolve, maxEnd);
  });
  useEffect(() => {
    if (!animate) { resetAll(); return; }
    const DISASSEMBLE = [...TREE_SEQ].reverse().map(([id,type,val,dur,delay],i) => {
      const rv = type==="dash" ? (id==="trunk" ? 120 : 60) : 0;
      return [id, type, rv, Math.round(dur*0.8), i*60];
    });
    let alive = true;
    async function loop() {
      while (alive) {
        resetToEmpty(); await new Promise(r => setTimeout(r, 50));
        await runSeq(TREE_SEQ); await new Promise(r => setTimeout(r, 800));
        await runSeq(DISASSEMBLE); await new Promise(r => setTimeout(r, 400));
      }
    }
    loop();
    return () => { alive = false; };
  }, [animate]);
  const r = (id) => ({ ref: refs[id] });
  return (
    <svg viewBox="0 0 100 110" width="48" height="53" style={{display:"block"}}>
      <path {...r("root2")} d="M50 73 Q50 80 50 86 Q50 91 49 95" fill="none" stroke="#5C7A6E" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="60" strokeDashoffset="60" opacity="0"/>
      <path {...r("root1")} d="M48 72 Q40 78 32 82 Q25 85 18 84" fill="none" stroke="#5C7A6E" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="60" strokeDashoffset="60" opacity="0"/>
      <path {...r("root3")} d="M52 72 Q60 78 68 82 Q75 85 82 84" fill="none" stroke="#5C7A6E" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="60" strokeDashoffset="60" opacity="0"/>
      <circle {...r("rd1")} cx="18" cy="84" r="2.2" fill="#C4774A" opacity="0"/>
      <circle {...r("rd2")} cx="49" cy="95" r="2.2" fill="#C4774A" opacity="0"/>
      <circle {...r("rd3")} cx="82" cy="84" r="2.2" fill="#C4774A" opacity="0"/>
      <path {...r("bookL")} d="M22 68 Q30 61 48 58 L48 67 Q31 69 24 75 Z" fill="#5C7A6E" opacity="0"/>
      <path {...r("bookR")} d="M78 68 Q70 61 52 58 L52 67 Q69 69 76 75 Z" fill="#5C7A6E" opacity="0"/>
      <line {...r("spine")} x1="50" y1="58" x2="50" y2="70" stroke="#F7F5F2" strokeWidth="1.2" opacity="0"/>
      <path {...r("trunk")} d="M48 58 Q47 46 46 34 Q46 26 47 18" fill="none" stroke="#5C7A6E" strokeWidth="4.5" strokeLinecap="round" strokeDasharray="120" strokeDashoffset="120" opacity="0"/>
      <path {...r("br1")} d="M48 46 Q38 41 28 38" fill="none" stroke="#5C7A6E" strokeWidth="2.8" strokeLinecap="round" strokeDasharray="60" strokeDashoffset="60" opacity="0"/>
      <path {...r("br2")} d="M52 46 Q62 41 72 38" fill="none" stroke="#5C7A6E" strokeWidth="2.8" strokeLinecap="round" strokeDasharray="60" strokeDashoffset="60" opacity="0"/>
      <path {...r("br3")} d="M47 32 Q37 26 28 22" fill="none" stroke="#5C7A6E" strokeWidth="2" strokeLinecap="round" strokeDasharray="60" strokeDashoffset="60" opacity="0"/>
      <path {...r("br4")} d="M53 32 Q63 26 72 22" fill="none" stroke="#5C7A6E" strokeWidth="2" strokeLinecap="round" strokeDasharray="60" strokeDashoffset="60" opacity="0"/>
      <ellipse {...r("l1")} cx="24" cy="35" rx="6.5" ry="9" fill="#5C7A6E" transform="rotate(-30 24 35)" opacity="0"/>
      <ellipse {...r("l2")} cx="16" cy="30" rx="4.5" ry="6.5" fill="#C4774A" transform="rotate(-40 16 30)" opacity="0"/>
      <ellipse {...r("l3")} cx="76" cy="35" rx="6.5" ry="9" fill="#5C7A6E" transform="rotate(30 76 35)" opacity="0"/>
      <ellipse {...r("l4")} cx="84" cy="30" rx="4.5" ry="6.5" fill="#C4774A" transform="rotate(40 84 30)" opacity="0"/>
      <ellipse {...r("l5")} cx="26" cy="19" rx="5.5" ry="7.5" fill="#5C7A6E" transform="rotate(-25 26 19)" opacity="0"/>
      <ellipse {...r("l6")} cx="74" cy="19" rx="5.5" ry="7.5" fill="#5C7A6E" transform="rotate(25 74 19)" opacity="0"/>
      <ellipse {...r("l7")} cx="50" cy="12" rx="8" ry="10" fill="#5C7A6E" opacity="0"/>
      <ellipse {...r("l8")} cx="42" cy="8" rx="4.5" ry="6.5" fill="#C4774A" transform="rotate(-15 42 8)" opacity="0"/>
      <ellipse {...r("l9")} cx="58" cy="8" rx="4.5" ry="6.5" fill="#C4774A" transform="rotate(15 58 8)" opacity="0"/>
    </svg>
  );
}

// ── Ornament ───────────────────────────────────────────────────────────────────

function Ornament() {
  return (
    <div style={{display:"flex",alignItems:"center",gap:"10px",margin:"16px 0"}}>
      <div style={{flex:1,height:"1px",background:C.border}}/>
      <div style={{width:"5px",height:"5px",background:C.ornament,transform:"rotate(45deg)",flexShrink:0}}/>
      <div style={{flex:1,height:"1px",background:C.border}}/>
    </div>
  );
}

// ── Language Dropdown ──────────────────────────────────────────────────────────

const LANG_NAMES = { ru:"Русский", en:"English" };

function LanguageDropdown({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <div ref={ref} style={{position:"relative"}}>
      <button onClick={()=>setOpen(o=>!o)} style={{display:"flex",alignItems:"center",gap:"5px",background:"transparent",border:`1px solid ${D.border}`,borderRadius:"20px",padding:"3px 10px",cursor:"pointer",fontSize:"11px",color:D.sec,fontFamily:D.ui}}>
        <span style={{fontSize:"13px"}}>🌐</span>
        {LANG_NAMES[lang]}
        <span style={{fontSize:"9px",transition:"transform .2s",transform:open?"rotate(180deg)":"none"}}>▾</span>
      </button>
      {open && (
        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,background:D.panel,border:`1px solid ${D.border}`,borderRadius:"12px",padding:"4px",minWidth:"120px",boxShadow:"0 4px 16px rgba(0,0,0,0.4)",zIndex:50}}>
          {Object.entries(LANG_NAMES).map(([code, name]) => (
            <div key={code} onClick={()=>{ setLang(code); setOpen(false); }}
              style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 10px",borderRadius:"8px",cursor:"pointer",fontSize:"12px",fontFamily:D.ui,background:lang===code?D.panel2:"transparent",color:lang===code?D.h:D.sec,fontWeight:lang===code?"500":"400"}}
              onMouseEnter={e=>e.currentTarget.style.background=D.panel2}
              onMouseLeave={e=>e.currentTarget.style.background=lang===code?D.panel2:"transparent"}
            >
              {name}
              {lang===code && <span style={{fontSize:"11px"}}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OnboardingModal({ t, lang, onDone, onLangChange }) {
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState([]);
  const [onboardingEmail, setOnboardingEmail] = useState("");
  const [onboardingMagicSent, setOnboardingMagicSent] = useState(false);

  const toggleInterest = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleOnboardingMagicLink = async () => {
    if (!onboardingEmail.trim()) return;
    const { error } = await supabase.auth.signInWithOtp({
      email: onboardingEmail,
      options: { emailRedirectTo: 'https://wiki-tale.app' }
    });
    if (!error) setOnboardingMagicSent(true);
  };

  const handleDone = () => {
    localStorage.setItem('wt_onboarding_done', '1');
    localStorage.setItem('wt_interests', JSON.stringify(selected));
    onDone(selected);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:1000,padding:"0"}}>
      <div style={{background:D.panel,borderRadius:"20px 20px 0 0",padding:"24px 20px 32px",width:"100%",maxWidth:"480px",maxHeight:"90vh",overflowY:"auto",animation:"wt_slideup .35s ease",border:`1px solid ${D.border}`,borderBottom:"none"}}>

        <div style={{display:"flex",gap:"6px",justifyContent:"center",marginBottom:"20px"}}>
          {[1,2,3].map(s => (
            <div key={s} style={{width: s === step ? "20px" : "6px",height:"6px",borderRadius:"3px",background: s === step ? D.ember : D.border,transition:"all .3s"}}/>
          ))}
        </div>

        {step === 1 && (
          <div style={{animation:"wt_fadein .3s ease"}}>
            <div style={{textAlign:"center",marginBottom:"20px"}}>
              <p style={{fontFamily:D.serif,fontSize:"22px",color:D.h,letterSpacing:"0.15em",margin:"0 0 4px"}}>{t.onboardingStep1Title}</p>
              <p style={{fontSize:"13px",color:D.sec,margin:0,fontFamily:D.ui}}>{t.onboardingStep1Sub}</p>
            </div>

            <div style={{marginBottom:"20px"}}>
              <p style={{fontSize:"10px",letterSpacing:"0.14em",textTransform:"uppercase",color:D.ter,margin:"0 0 10px",fontFamily:D.ui}}>
                {lang==="ru"?"Язык историй":"Story language"}
              </p>
              <div style={{display:"flex",gap:"8px"}}>
                {[
                  {code:"ru", label:"Русский"},
                  {code:"en", label:"English"},
                ].map(l => (
                  <button key={l.code} onClick={() => onLangChange(l.code)} style={{
                    flex:1, padding:"10px 8px", borderRadius:"10px", cursor:"pointer",
                    fontFamily:D.ui, fontSize:"13px",
                    border:`1px solid ${lang===l.code?D.ember:D.border}`,
                    background:lang===l.code?"rgba(196,119,74,.14)":"transparent",
                    color:lang===l.code?D.emberBright:D.sec,
                    transition:"all .15s",
                  }}>{l.label}</button>
                ))}
              </div>
            </div>

            <div style={{display:"flex",flexDirection:"column",marginBottom:"24px"}}>
              {t.onboardingStep1Items.map((item, i) => (
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:"12px",padding:"10px 0",borderBottom:i<t.onboardingStep1Items.length-1?`1px solid ${D.borderSoft}`:"none",cursor:"default"}}>
                  <span style={{fontSize:"18px",flexShrink:0,opacity:0.7}}>{item.icon}</span>
                  <div>
                    <p style={{fontFamily:D.serif,fontSize:"14px",color:D.sec,margin:"0 0 2px"}}>{item.title}</p>
                    <p style={{fontSize:"12px",color:D.ter,margin:0,fontFamily:D.ui}}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setStep(2)} style={{width:"100%",padding:"12px",background:D.ember,color:"#fff",border:"none",borderRadius:"12px",fontFamily:D.serif,fontSize:"15px",cursor:"pointer"}}>{t.onboardingNext}</button>
          </div>
        )}

        {step === 2 && (
          <div style={{animation:"wt_fadein .3s ease"}}>
            <p style={{fontFamily:D.serif,fontSize:"18px",color:D.h,margin:"0 0 4px"}}>{t.onboardingStep2Title}</p>
            <p style={{fontSize:"12px",color:D.sec,margin:"0 0 16px",fontFamily:D.ui}}>{t.onboardingStep2Sub}</p>
            <div style={{display:"flex",flexWrap:"wrap",gap:"8px",marginBottom:"24px"}}>
              {DOMAINS.map(domain => (
                <button key={domain.id} onClick={() => toggleInterest(domain.id)} style={{
                  padding:"8px 14px",borderRadius:"20px",fontSize:"13px",cursor:"pointer",fontFamily:D.ui,
                  border:`1px solid ${selected.includes(domain.id) ? domain.color : D.border}`,
                  background: selected.includes(domain.id) ? domain.color + "22" : "transparent",
                  color: selected.includes(domain.id) ? domain.color : D.sec,
                  transition:"all .15s",
                }}>
                  {domain.icon} {domain.name[lang]}
                </button>
              ))}
            </div>
            <button onClick={() => setStep(3)} style={{width:"100%",padding:"12px",background:D.ember,color:"#fff",border:"none",borderRadius:"12px",fontFamily:D.serif,fontSize:"15px",cursor:"pointer"}}>{t.onboardingNext}</button>
            <button onClick={handleDone} style={{width:"100%",padding:"8px",background:"transparent",border:"none",color:D.ter,fontSize:"12px",cursor:"pointer",fontFamily:D.ui,marginTop:"8px"}}>{t.onboardingSkip}</button>
          </div>
        )}

        {step === 3 && (
          <div style={{animation:"wt_fadein .3s ease"}}>
            <p style={{fontFamily:D.serif,fontSize:"18px",color:D.h,margin:"0 0 4px"}}>{t.onboardingStep3Title}</p>
            <p style={{fontSize:"12px",color:D.sec,margin:"0 0 20px",fontFamily:D.ui}}>{t.onboardingStep3Sub}</p>

            <div style={{display:"flex",flexDirection:"column",gap:"8px",marginBottom:"12px"}}>
              <button onClick={()=>{
                localStorage.setItem('wt_onboarding_done', '1');
                localStorage.setItem('wt_interests', JSON.stringify(selected));
                supabase.auth.signInWithOAuth({ provider:'google', options:{ redirectTo:'https://wiki-tale.app' } });
              }} style={{width:"100%",padding:"11px",background:D.panel2,border:`1px solid ${D.border}`,borderRadius:"12px",fontFamily:D.ui,fontSize:"13px",cursor:"pointer",color:D.h,display:"flex",alignItems:"center",justifyContent:"center",gap:"8px"}}>
                <span>G</span> {lang==="ru"?"Войти через Google":lang==="de"?"Mit Google anmelden":"Continue with Google"}
              </button>
              <div style={{display:"flex",gap:"8px"}}>
                <input type="email" placeholder="your@email.com" value={onboardingEmail} onChange={e=>setOnboardingEmail(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&handleOnboardingMagicLink()}
                  style={{flex:1,background:D.panel3,border:`1px solid ${D.border}`,borderRadius:"10px",padding:"10px 14px",fontSize:"13px",color:D.h,fontFamily:D.ui,outline:"none"}}/>
                <button onClick={handleOnboardingMagicLink} style={{background:D.ember,color:"#fff",border:"none",borderRadius:"10px",padding:"10px 18px",fontSize:"13px",cursor:"pointer",fontFamily:D.serif}}>→</button>
              </div>
              {onboardingMagicSent && (
                <p style={{fontSize:"12px",color:D.emberBright,fontFamily:D.ui,textAlign:"center",margin:0}}>
                  {lang==="ru"?"✉️ Проверь почту":lang==="de"?"✉️ Prüfe dein Postfach":"✉️ Check your inbox"}
                </p>
              )}
            </div>

            <button onClick={handleDone} style={{width:"100%",padding:"10px",background:"transparent",border:"none",color:D.ter,fontSize:"12px",cursor:"pointer",fontFamily:D.ui}}>
              {lang==="ru"?"Пропустить, войду позже":lang==="de"?"Überspringen":"Skip for now"}
            </button>

            <p style={{fontSize:"11px",color:D.ter,fontFamily:D.ui,textAlign:"center",margin:"12px 0 0"}}>
              {lang==="ru"
                ? "📧 После входа можно подписаться на письмо дня — кнопка рядом с выходом"
                : "📧 After signing in you can subscribe to daily digest — button next to sign out"}
            </p>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Home Screen (dark, prototype-aligned) ────────────────────────────────────

function HomeScreen({ t, lang, interests, trackProgress, xp, level, streak, onStartCard, onStartTrack, onBrowseTracks, onSearch, onOpenDailyStory }) {
  const [dailyStories, setDailyStories] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(true);
  const fallbackCards = getDailyCards(interests, lang);

  useEffect(() => {
    setDailyLoading(true);
    setDailyStories([]);
    const today = new Date().toISOString().split('T')[0];
    const presets = ['popscience', 'eli8', 'academic'];
    Promise.all(
      presets.map(preset =>
        supabase
          .from('daily_stories')
          .select('id, preset, title, story, wiki_url, lang')
          .eq('date', today)
          .eq('lang', lang)
          .eq('preset', preset)
          .limit(1)
          .maybeSingle()
          .then(({ data }) => data)
      )
    ).then(results => {
      setDailyStories(results.filter(Boolean));
      setDailyLoading(false);
    });
  }, [lang]);

  const getActiveTrack = () => {
    for (const domain of DOMAINS) {
      for (const track of domain.tracks) {
        const key = `${domain.id}_${track.id}`;
        const prog = trackProgress[key] || 0;
        if (prog > 0 && prog < track.articles.length) {
          return { domain, track, articleIndex: prog };
        }
      }
    }
    return null;
  };

  const activeTrack = getActiveTrack();
  const presetIds = ["popscience", "eli8", "academic"];

  // XP bar %: same LEVELS thresholds as GameScreen
  const LEVELS = [
    { level: 1, min: 0,   max: 50   },
    { level: 2, min: 50,  max: 150  },
    { level: 3, min: 150, max: 350  },
    { level: 4, min: 350, max: 9999 },
  ];
  const curL = LEVELS.find(l => l.level === level) || LEVELS[0];
  const nextL = LEVELS.find(l => l.level === level + 1);
  const xpPct = nextL ? Math.max(0, Math.min(100, Math.round(((xp - curL.min) / (nextL.min - curL.min)) * 100))) : 100;

  return (
    // Full-width dark section under the app header
    <div style={{
      background: D.bg, color: D.body, fontFamily: D.ui,
      minHeight:"calc(100vh - 80px)", // fill viewport minus tab bar area
      paddingTop:"20px", paddingBottom:"20px",
    }}>

      {/* Greeting row */}
      <div style={{
        display:"flex", alignItems:"flex-start", justifyContent:"space-between",
        padding:"4px 20px 10px", maxWidth:"620px", margin:"0 auto", width:"100%",
      }}>
        <div style={{fontSize:13, color:D.sec, letterSpacing:".01em", fontFamily:D.serif}}>
          {lang==="ru" ? "Что учим сегодня?" : "What are we learning today?"}
        </div>
        {streak > 0 && (
          <div style={{
            display:"flex", alignItems:"center", gap:5,
            background:"rgba(196,119,74,.14)", border:"1px solid rgba(196,119,74,.3)",
            padding:"6px 11px", borderRadius:999, flexShrink:0, marginLeft:12,
          }}>
            <span style={{fontSize:14}}>🔥</span>
            <span style={{fontWeight:700, fontSize:15, color:D.emberBright}}>{streak}</span>
          </div>
        )}
      </div>

      {/* XP bar */}
      <div style={{maxWidth:"620px", margin:"0 auto", padding:"0 20px 16px", width:"100%"}}>
        <div style={{height:3, background:"rgba(255,255,255,.08)", borderRadius:2, overflow:"hidden"}}>
          <div style={{
            height:"100%",
            width:`${xpPct}%`,
            background:`linear-gradient(90deg,${D.ember},${D.emberBright})`,
            borderRadius:2, transition:"width .4s ease",
          }}/>
        </div>
      </div>

      {/* Body */}
      <div style={{maxWidth:"620px", margin:"0 auto", padding:"0 18px", width:"100%"}}>

        {/* Continue your journey */}
        {activeTrack && (
          <>
            <p style={{fontSize:9, letterSpacing:".14em", textTransform:"uppercase", color:D.ter2, margin:"0 0 10px"}}>
              {lang==="ru" ? "Продолжи путешествие" : "Continue your journey"}
            </p>
            <div
              onClick={() => onStartTrack(activeTrack.domain, activeTrack.track, activeTrack.articleIndex)}
              style={{
                background:"linear-gradient(150deg,#1a1410,#12161f)",
                border:"1px solid rgba(196,119,74,.25)",
                borderLeft:`3px solid ${D.ember}`,
                borderRadius:16, padding:16, marginBottom:18, cursor:"pointer",
              }}
            >
              <div style={{fontSize:9, letterSpacing:".12em", textTransform:"uppercase", color:"#8A6A50", marginBottom:6}}>
                {activeTrack.domain.name[lang]} · {lang==="ru"?"глава":"chapter"} {activeTrack.articleIndex + 1}/{activeTrack.track.articles.length}
              </div>
              <div style={{fontFamily:D.serif, fontWeight:600, fontSize:16, color:D.h, marginBottom:3}}>
                {activeTrack.track.name[lang]}
              </div>
              <div style={{fontSize:11, color:D.sec2, marginBottom:12}}>
                {lang==="ru"?"Дальше":"Next"}: {activeTrack.track.articles[activeTrack.articleIndex][lang]} · 5 min
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onStartTrack(activeTrack.domain, activeTrack.track, activeTrack.articleIndex); }}
                style={{
                  width:"100%", background:D.ember, color:"#fff", border:"none",
                  borderRadius:10, padding:11, fontWeight:600, fontSize:14, cursor:"pointer",
                  fontFamily:D.ui,
                }}
              >
                {lang==="ru" ? "Читать следующую главу →" : "Read next chapter →"}
              </button>
            </div>
          </>
        )}

        {/* Story of the day */}
        <p style={{fontSize:9, letterSpacing:".14em", textTransform:"uppercase", color:D.ter2, margin:"0 0 10px"}}>
          {lang==="ru" ? "История дня" : "Story of the day"}
        </p>
        <div style={{display:"flex", flexDirection:"column", gap:7, marginBottom:20}}>
          {dailyLoading ? (
            [0,1,2].map(i => (
              <div key={i} style={{
                height:52, borderRadius:12,
                background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.06)",
              }}/>
            ))
          ) : dailyStories.length > 0 ? (
            dailyStories.map((story, i) => {
              const presetId = story.preset || presetIds[i % presetIds.length];
              const preset = PRESETS.find(p => p.id === presetId);
              const presetColor = PRESET_COLORS[presetId] || D.ember;
              const presetName = preset ? t.presets[PRESETS.indexOf(preset)] : presetId;
              return (
                <div key={story.id}
                  onClick={() => onOpenDailyStory(story)}
                  style={{
                    background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.06)",
                    borderLeft:`2px solid ${presetColor}`,
                    borderRadius:12, padding:"12px 14px", cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
                  }}
                >
                  <div style={{flex:1, minWidth:0}}>
                    <p style={{fontFamily:D.serif, fontWeight:500, fontSize:13, color:D.body, margin:"0 0 2px"}}>
                      {story.title}
                    </p>
                    <p style={{fontSize:10, color:D.ter, margin:0}}>{presetName} · ~5 min</p>
                  </div>
                  <span style={{color:D.ter2, fontSize:14, flexShrink:0}}>→</span>
                </div>
              );
            })
          ) : (
            fallbackCards.map((card, i) => {
              const presetId = presetIds[i % presetIds.length];
              const preset = PRESETS.find(p => p.id === presetId);
              const presetColor = PRESET_COLORS[presetId] || D.ember;
              const presetName = t.presets[PRESETS.indexOf(preset)];
              return (
                <div key={i}
                  onClick={() => onStartCard(card, preset)}
                  style={{
                    background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.06)",
                    borderLeft:`2px solid ${presetColor}`,
                    borderRadius:12, padding:"12px 14px", cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
                  }}
                >
                  <div style={{flex:1, minWidth:0}}>
                    <p style={{fontFamily:D.serif, fontWeight:500, fontSize:13, color:D.body, margin:"0 0 2px"}}>
                      {card.article[lang]}
                    </p>
                    <p style={{fontSize:10, color:D.ter, margin:0}}>{presetName} · ~5 min</p>
                  </div>
                  <span style={{color:D.ter2, fontSize:14, flexShrink:0}}>→</span>
                </div>
              );
            })
          )}
        </div>

        {/* Two more ways in */}
        <p style={{fontSize:9, letterSpacing:".14em", textTransform:"uppercase", color:D.ter2, margin:"0 0 10px"}}>
          {t.twoMoreWaysLabel}
        </p>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:20}}>
          <div onClick={onBrowseTracks}
            style={{
              background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.06)",
              borderRadius:14, padding:"16px 12px", textAlign:"center", cursor:"pointer",
            }}
          >
            <div style={{fontSize:22, marginBottom:8}}>📚</div>
            <p style={{fontSize:13, fontWeight:500, margin:"0 0 2px", color:D.body}}>{t.knowledgeDomainsLabel}</p>
            <p style={{fontSize:11, color:D.ter, margin:0}}>{t.knowledgeDomainsSub}</p>
          </div>
          <div onClick={onSearch}
            style={{
              background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.06)",
              borderRadius:14, padding:"16px 12px", textAlign:"center", cursor:"pointer",
            }}
          >
            <div style={{fontSize:22, marginBottom:8}}>🔍</div>
            <p style={{fontSize:13, fontWeight:500, margin:"0 0 2px", color:D.body}}>{t.yourOwnStory}</p>
            <p style={{fontSize:11, color:D.ter, margin:0}}>{t.yourOwnStorySub}</p>
          </div>
        </div>

      </div>
    </div>
  );
}

// ── Track Navigator ────────────────────────────────────────────────────────────

function TrackNavigator({ t, lang, activeTrack, setActiveTrack, onJumpTo, onChangeStyle, styleLabel }) {
  const [open, setOpen] = useState(false);
  if (!activeTrack) return null;
  const { domain, track, articleIndex } = activeTrack;
  const total = track.articles.length;
  const pct = Math.round((articleIndex / Math.max(total - 1, 1)) * 100);

  return (
    <div style={{marginBottom:"12px"}}>
      <div onClick={()=>setOpen(o=>!o)} style={{
        display:"flex",alignItems:"center",justifyContent:"space-between",
        background:D.panel,borderRadius:"20px",padding:"8px 14px",cursor:"pointer",
        border:`1px solid ${D.border}`,
      }}>
        <div style={{display:"flex",alignItems:"center",gap:"8px",minWidth:0}}>
          <span style={{fontSize:"14px",flexShrink:0}}>{domain.icon}</span>
          <span style={{fontSize:"13px",fontWeight:"500",color:D.h,fontFamily:D.ui,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{track.name[lang]}</span>
          <span style={{fontSize:"11px",color:D.ter,fontFamily:D.ui,flexShrink:0}}>{articleIndex+1} {t.of} {total}</span>
        </div>
        <span style={{fontSize:"11px",color:D.ter,transition:"transform .2s",transform:open?"rotate(180deg)":"none",flexShrink:0,marginLeft:"8px"}}>▾</span>
      </div>

      {open && (
        <div style={{background:D.panel2,border:`1px solid ${D.border}`,borderRadius:"16px",padding:"16px",marginTop:"8px"}}>

          <div style={{display:"flex",alignItems:"center",gap:"8px",marginBottom:"14px"}}>
            <button
              disabled={articleIndex<=0}
              onClick={()=>onJumpTo(articleIndex-1)}
              style={{width:"32px",height:"32px",borderRadius:"50%",border:`1px solid ${D.border}`,background:"transparent",cursor:articleIndex<=0?"default":"pointer",opacity:articleIndex<=0?0.3:1,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"14px",color:D.sec,flexShrink:0}}
            >‹</button>
            <div style={{flex:1,height:"4px",background:D.border,borderRadius:"2px",overflow:"hidden"}}>
              <div style={{height:"100%",width:`${pct}%`,background:domain.color,borderRadius:"2px",transition:"width .3s"}}/>
            </div>
            <button
              disabled={articleIndex>=total-1}
              onClick={()=>onJumpTo(articleIndex+1)}
              style={{width:"32px",height:"32px",borderRadius:"50%",border:`1px solid ${D.border}`,background:"transparent",cursor:articleIndex>=total-1?"default":"pointer",opacity:articleIndex>=total-1?0.3:1,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"14px",color:D.sec,flexShrink:0}}
            >›</button>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:"5px",marginBottom:"14px"}}>
            {track.articles.map((article, i) => {
              const isDone = i < articleIndex;
              const isCurrent = i === articleIndex;
              return (
                <div key={i}
                  onClick={()=>onJumpTo(i)}
                  style={{
                    display:"flex",alignItems:"center",gap:"10px",padding:"9px 10px",
                    borderRadius:"10px",cursor:"pointer",
                    background:isCurrent?`${domain.color}18`:"transparent",
                    border:isCurrent?`1px solid ${domain.color}`:"1px solid transparent",
                    opacity:isDone?0.55:1,
                  }}
                >
                  <div style={{
                    width:"20px",height:"20px",borderRadius:"50%",flexShrink:0,
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:"10px",fontFamily:D.ui,
                    background:isDone?domain.color:isCurrent?`${domain.color}25`:D.panel3,
                    color:isDone?"#fff":isCurrent?domain.color:D.ter,
                  }}>
                    {isDone ? "✓" : i+1}
                  </div>
                  <span style={{fontSize:"13px",fontFamily:D.serif,color:isCurrent?D.h:D.sec,fontWeight:isCurrent?"500":"400",flex:1}}>{article[lang]}</span>
                  {isCurrent && <span style={{fontSize:"12px",color:domain.color}}>→</span>}
                </div>
              );
            })}
          </div>

          <div style={{borderTop:`1px solid ${D.border}`,paddingTop:"12px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <p style={{fontSize:"10px",color:D.ter,margin:"0 0 2px",fontFamily:D.ui}}>{lang==="ru"?"Стиль для трека":lang==="de"?"Stil für diesen Track":"Style for this track"}</p>
              <p style={{fontSize:"13px",fontWeight:"500",margin:0,fontFamily:D.ui,color:D.h}}>{styleLabel || "—"}</p>
            </div>
            <button onClick={onChangeStyle} style={{fontSize:"12px",padding:"6px 14px",borderRadius:"10px",border:`1px solid ${D.border}`,background:"transparent",color:D.sec,cursor:"pointer",fontFamily:D.ui}}>
              {lang==="ru"?"Сменить":lang==="de"?"Ändern":"Change"}
            </button>
          </div>

        </div>
      )}
    </div>
  );
}

// ── ReadingView: dark, page-by-page reading (fullscreen overlay) ─────────────
function ReadingView({ lang, result, activeTrack, pages, pageIndex, setPageIndex, onFinish, onExit, presetLabel, onChangeStyle }) {
  const total = pages.length;
  const domain = activeTrack?.domain;
  const domainLabel = domain
    ? (domain.name?.[lang] || domain.name?.en || "")
    : (lang === "ru" ? "История дня" : "Story of the day");
  const isLast = pageIndex >= total - 1;

  // B2: chapter-header image, fetched live from Wikipedia on open — no DB
  // storage. Only shown once, on page 1, since library stories (Sonnet) never
  // embed [[IMG:n]] markers the way on-demand prompts.js stories do — there's
  // no per-paragraph placement info to work with, so this is a cover image,
  // not a distributed one. Skipped entirely if the page already has a
  // per-page image from markers (on-demand stories), to avoid stacking two.
  const [headerImage, setHeaderImage] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setHeaderImage(null);
    if (result?.title) {
      getArticleImages(result.title, lang, 1).then(imgs => {
        if (!cancelled && imgs.length) setHeaderImage(imgs[0]);
      }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [result?.title, lang]);

  // Analytics: single choke point for ALL reading paths (library, on-demand,
  // hook, daily/shared deep links) — ReadingView is the one component every
  // path renders into, so tracking here answers the retention-spec question
  // without duplicating capture calls at every entry point. chapter_opened
  // fires once per unique story (keyed on id/title, not on every re-render);
  // scroll_depth fires on unmount with however far the reader actually got —
  // catches both "clicked exit" and "clicked test yourself" as an exit, and
  // anything in between (closing the tab mid-chapter won't fire this, that's
  // an unavoidable limitation of client-side unmount-based tracking).
  const maxPageReachedRef = useRef(0);
  useEffect(() => {
    maxPageReachedRef.current = Math.max(maxPageReachedRef.current, pageIndex);
  }, [pageIndex]);

  useEffect(() => {
    maxPageReachedRef.current = 0;
    try {
      window.posthog?.capture('chapter_opened', {
        story_id: result?.id || null, title: result?.title, total_pages: total,
        from_track: !!activeTrack,
      });
    } catch (e) {}
    return () => {
      const reached = maxPageReachedRef.current + 1; // 1-indexed for readability
      try {
        window.posthog?.capture('scroll_depth', {
          story_id: result?.id || null, title: result?.title,
          max_page_reached: reached, total_pages: total,
          percent: total > 0 ? Math.round((reached / total) * 100) : 0,
        });
      } catch (e) {}
    };
  }, [result?.id, result?.title]);

  const wrap = {
    position: "fixed", inset: 0, background: D.bg, zIndex: 200,
    display: "flex", flexDirection: "column",
    color: D.body, fontFamily: D.ui, overflowY: "auto",
  };

  return (
    <div style={wrap}>
      {/* Exit button */}
      <button onClick={onExit} aria-label={lang === "ru" ? "Выйти" : "Exit"}
        style={{
          position: "fixed", top: 14, right: 14, zIndex: 220,
          width: 36, height: 36, borderRadius: "50%",
          background: "rgba(255,255,255,.04)", border: `1px solid ${D.border}`,
          color: D.sec, cursor: "pointer", fontSize: 18, lineHeight: 1,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>×</button>

      {/* Page dots */}
      <div style={{ display: "flex", gap: 6, justifyContent: "center", padding: "18px 16px 4px" }}>
        {pages.map((_, i) => (
          <div key={i} style={{
            height: 3, borderRadius: 2, flex: 1, maxWidth: 60,
            background: i < pageIndex ? D.ember : i === pageIndex ? D.emberBright : D.border,
            transition: "background .2s",
          }}/>
        ))}
      </div>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "12px 22px 120px", width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, marginBottom: 4 }}>
          <p style={{
            margin: 0, fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase",
            color: domain?.color || D.ember, fontWeight: 600,
          }}>{domainLabel}</p>
          {activeTrack && presetLabel && onChangeStyle && (
            <button onClick={onChangeStyle} style={{
              display: "flex", alignItems: "center", gap: 5,
              background: "rgba(196,119,74,.12)", border: "1px solid rgba(196,119,74,.35)",
              borderRadius: 20, padding: "5px 11px", color: D.emberBright,
              fontSize: 11, fontWeight: 600, fontFamily: D.ui, cursor: "pointer", flexShrink: 0,
            }}>
              {presetLabel}
            </button>
          )}
        </div>
        <h1 style={{
          margin: "0 0 4px", fontFamily: D.serif, fontWeight: 600, fontSize: 26,
          color: D.h, lineHeight: 1.2,
        }}>{result.title}</h1>
        <p style={{ margin: "0 0 22px", fontSize: 12, color: D.sec }}>
          {lang === "ru" ? "Страница" : "Page"} {pageIndex + 1}/{total}
        </p>

        {pages[pageIndex]?.image ? (
          <figure style={{ margin: "0 0 20px" }}>
            <img
              src={pages[pageIndex].image.url}
              alt={pages[pageIndex].image.caption || ""}
              loading="lazy"
              style={{
                width: "100%", maxHeight: 320, objectFit: "cover",
                borderRadius: 14, border: `1px solid ${D.border}`, display: "block",
              }}
              onError={(e) => { e.currentTarget.closest("figure").style.display = "none"; }}
            />
            {pages[pageIndex].image.caption && (
              <figcaption style={{ fontSize: 11, color: D.ter, marginTop: 6, textAlign: "center" }}>
                {pages[pageIndex].image.caption}
              </figcaption>
            )}
          </figure>
        ) : (pageIndex === 0 && headerImage) && (
          <figure style={{ margin: "0 0 20px" }}>
            <img
              src={headerImage.url}
              alt={headerImage.caption || ""}
              loading="lazy"
              style={{
                width: "100%", maxHeight: 320, objectFit: "cover",
                borderRadius: 14, border: `1px solid ${D.border}`, display: "block",
              }}
              onError={(e) => { e.currentTarget.closest("figure").style.display = "none"; }}
            />
            {headerImage.caption && (
              <figcaption style={{ fontSize: 11, color: D.ter, marginTop: 6, textAlign: "center" }}>
                {headerImage.caption}
              </figcaption>
            )}
          </figure>
        )}

        {(() => {
          // B2: split this page's paragraphs so a thin neural-motif divider can
          // sit every ~3 paragraphs, and the page's pull-quote (if any — see
          // attachPullQuote in paginateStory) renders as a styled callout right
          // after the paragraph that actually contains it.
          const paras = (pages[pageIndex]?.text || "").split(/\n\s*\n/).filter(Boolean);
          const quote = pages[pageIndex]?.pullQuote;
          const quoteParaIdx = quote
            ? paras.findIndex(p => p.includes(quote))
            : -1;

          return paras.map((para, i) => (
            <div key={i}>
              <div style={{
                fontFamily: D.serif, fontSize: 19, lineHeight: 1.8,
                color: D.body, whiteSpace: "pre-wrap", marginBottom: 18,
              }}>
                {para}
              </div>

              {i === quoteParaIdx && (
                <blockquote style={{
                  margin: "4px 0 22px", padding: "2px 0 2px 18px",
                  borderLeft: `2px solid ${D.ember}`,
                  fontFamily: D.serif, fontStyle: "italic", fontSize: 22,
                  lineHeight: 1.5, color: D.h,
                }}>
                  {quote}
                </blockquote>
              )}

              {i > 0 && i < paras.length - 1 && (i + 1) % 3 === 0 && (
                <div style={{ display: "flex", justifyContent: "center", margin: "8px 0 22px", opacity: 0.3 }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="2" fill={D.ember} />
                    <line x1="8" y1="0" x2="8" y2="4" stroke={D.ember} strokeWidth="1" />
                    <line x1="8" y1="12" x2="8" y2="16" stroke={D.ember} strokeWidth="1" />
                    <line x1="0" y1="8" x2="4" y2="8" stroke={D.ember} strokeWidth="1" />
                    <line x1="12" y1="8" x2="16" y2="8" stroke={D.ember} strokeWidth="1" />
                  </svg>
                </div>
              )}
            </div>
          ));
        })()}

        {isLast && result.url && (
          <a href={result.url} target="_blank" rel="noopener noreferrer"
            style={{
              display: "inline-block", marginTop: 24, fontSize: 12, color: D.ter,
              textDecoration: "none", borderBottom: `1px solid ${D.border}`, paddingBottom: 2,
            }}>
            {lang === "ru" ? "Источник: Википедия ↗" : "Source: Wikipedia ↗"}
          </a>
        )}
      </div>

{/* Floating next button */}
      <button
        onClick={() => isLast ? onFinish() : setPageIndex(pageIndex + 1)}
        style={{
          position: "fixed", right: 20, bottom: 28, zIndex: 210,
          background: D.ember, color: "#fff", border: "none", borderRadius: 26,
          padding: "13px 26px", fontFamily: D.ui, fontWeight: 600, fontSize: 15, cursor: "pointer",
          boxShadow: "0 6px 20px rgba(196,119,74,.4)",
        }}>
        {isLast
          ? (lang === "ru" ? "Проверить себя →" : "Test yourself →")
          : (lang === "ru" ? "Дальше →" : "Next →")}
      </button>

      {/* Back to previous page */}
      {pageIndex > 0 && (
        <button onClick={() => setPageIndex(pageIndex - 1)}
          style={{
            position: "fixed", left: 20, bottom: 28, zIndex: 210, background: "transparent",
            color: D.sec2, border: `1px solid ${D.border}`, borderRadius: 26,
            padding: "13px 20px", fontFamily: D.ui, fontSize: 14, cursor: "pointer",
          }}>←</button>
      )}
    </div>
  );
}

// ── GrowingNeuronLoader: two neurons growing axons toward each other, connecting ──
function GrowingNeuronLoader({ lang, fact }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 0" }}>
      <svg viewBox="0 0 340 220" style={{ width: "100%", maxWidth: 300, display: "block" }}>
        <defs>
          <radialGradient id="gnl-somaL" cx="32%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#EDA870"/>
            <stop offset="55%" stopColor="#C4774A"/>
            <stop offset="100%" stopColor="#9A5A34"/>
          </radialGradient>
          <radialGradient id="gnl-somaR" cx="32%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#E8924E"/>
            <stop offset="55%" stopColor="#C4774A"/>
            <stop offset="100%" stopColor="#9A5A34"/>
          </radialGradient>
          <radialGradient id="gnl-nucleus" cx="40%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#5A3520"/>
            <stop offset="100%" stopColor="#3E2415"/>
          </radialGradient>
        </defs>

        {/* Dendrites, left cell */}
        <g stroke="#7A5A42" strokeWidth="1.1" fill="none" opacity="0.55" strokeLinecap="round">
          <path d="M52 108 Q35 95 22 82"/>
          <path d="M22 82 Q14 74 6 68"/>
          <path d="M22 82 Q16 90 10 96"/>
          <path d="M50 90 Q34 72 30 52"/>
          <path d="M30 52 Q26 40 20 30"/>
          <path d="M30 52 Q38 44 46 38"/>
          <path d="M48 130 Q30 138 16 152"/>
          <path d="M16 152 Q8 158 2 166"/>
          <path d="M65 138 Q56 158 60 178"/>
          <path d="M60 178 Q58 190 54 200"/>
        </g>
        {/* Dendrites, right cell */}
        <g stroke="#7A5A42" strokeWidth="1.1" fill="none" opacity="0.55" strokeLinecap="round">
          <path d="M288 108 Q305 95 318 82"/>
          <path d="M318 82 Q326 74 334 68"/>
          <path d="M318 82 Q324 90 330 96"/>
          <path d="M290 90 Q306 72 310 52"/>
          <path d="M310 52 Q314 40 320 30"/>
          <path d="M275 138 Q284 158 280 178"/>
        </g>

        {/* Left soma */}
        <g>
          <path d="M62 78 Q40 82 36 105 Q33 128 52 138 Q75 146 88 128 Q98 110 84 90 Q75 76 62 78 Z" fill="url(#gnl-somaL)" stroke="#7A4A2E" strokeWidth="0.8"/>
          <ellipse cx="60" cy="108" rx="13" ry="11" fill="url(#gnl-nucleus)" opacity="0.85"/>
          <path d="M62 78 Q40 82 36 105 Q33 128 52 138 Q75 146 88 128 Q98 110 84 90 Q75 76 62 78 Z" fill="#fff" opacity="0">
            <animate attributeName="opacity" values="0;0;.55;0" dur="3.4s" begin="0s" keyTimes="0;0.78;0.82;0.92" repeatCount="indefinite"/>
          </path>
        </g>
        {/* Right soma */}
        <g>
          <path d="M280 78 Q302 82 306 105 Q309 128 290 138 Q267 146 254 128 Q244 110 258 90 Q267 76 280 78 Z" fill="url(#gnl-somaR)" stroke="#7A4A2E" strokeWidth="0.8"/>
          <ellipse cx="282" cy="108" rx="13" ry="11" fill="url(#gnl-nucleus)" opacity="0.85"/>
          <path d="M280 78 Q302 82 306 105 Q309 128 290 138 Q267 146 254 128 Q244 110 258 90 Q267 76 280 78 Z" fill="#fff" opacity="0">
            <animate attributeName="opacity" values="0;0;.55;0" dur="3.4s" begin="0s" keyTimes="0;0.78;0.82;0.92" repeatCount="indefinite"/>
          </path>
        </g>

        {/* Growing axon, left → center */}
        <path d="M92 110 Q120 88 140 112 Q155 130 170 110"
          stroke="#E8924E" strokeWidth="2" fill="none" strokeLinecap="round"
          strokeDasharray="105" strokeDashoffset="105">
          <animate attributeName="stroke-dashoffset" values="105;0" dur="2.2s" begin="0.2s" fill="freeze" repeatCount="indefinite"/>
        </path>
        <circle r="4" fill="#E8924E" opacity="0">
          <animate attributeName="opacity" values="0;1;1;0" dur="2.2s" begin="0.2s" keyTimes="0;0.05;0.9;1" repeatCount="indefinite"/>
          <animateMotion dur="2.2s" begin="0.2s" repeatCount="indefinite" path="M92 110 Q120 88 140 112 Q155 130 170 110"/>
        </circle>

        {/* Growing axon, right → center */}
        <path d="M248 110 Q220 132 200 108 Q185 90 170 110"
          stroke="#E8924E" strokeWidth="2" fill="none" strokeLinecap="round"
          strokeDasharray="105" strokeDashoffset="105">
          <animate attributeName="stroke-dashoffset" values="105;0" dur="2.2s" begin="0.45s" fill="freeze" repeatCount="indefinite"/>
        </path>
        <circle r="4" fill="#E8924E" opacity="0">
          <animate attributeName="opacity" values="0;1;1;0" dur="2.2s" begin="0.45s" keyTimes="0;0.05;0.9;1" repeatCount="indefinite"/>
          <animateMotion dur="2.2s" begin="0.45s" repeatCount="indefinite" path="M248 110 Q220 132 200 108 Q185 90 170 110"/>
        </circle>

        {/* Return sparks after contact */}
        <circle r="3.5" fill="#fff" opacity="0">
          <animate attributeName="opacity" values="0;0;1;1;0" dur="3.4s" begin="0s" keyTimes="0;0.75;0.78;0.85;0.9" repeatCount="indefinite"/>
          <animateMotion dur="3.4s" begin="0s" repeatCount="indefinite" keyPoints="1;1;1;0;0" keyTimes="0;0.75;0.78;0.85;1" path="M170 110 Q155 130 140 112 Q120 88 92 110"/>
        </circle>
        <circle r="3.5" fill="#fff" opacity="0">
          <animate attributeName="opacity" values="0;0;1;1;0" dur="3.4s" begin="0s" keyTimes="0;0.75;0.78;0.85;0.9" repeatCount="indefinite"/>
          <animateMotion dur="3.4s" begin="0s" repeatCount="indefinite" keyPoints="1;1;1;0;0" keyTimes="0;0.75;0.78;0.85;1" path="M170 110 Q185 90 200 108 Q220 132 248 110"/>
        </circle>

        {/* Spark of connection */}
        <g opacity="0">
          <animate attributeName="opacity" values="0;0;1;0" dur="3.4s" begin="0s" keyTimes="0;0.77;0.8;0.95" repeatCount="indefinite"/>
          <circle cx="170" cy="110" r="30" fill="none" stroke="#E8DCC8" strokeWidth="1.6"/>
          <circle cx="170" cy="110" r="18" fill="none" stroke="#E8924E" strokeWidth="1.2"/>
          <line x1="170" y1="80" x2="170" y2="70" stroke="#E8DCC8" strokeWidth="1.4"/>
          <line x1="170" y1="140" x2="170" y2="150" stroke="#E8DCC8" strokeWidth="1.4"/>
          <line x1="140" y1="110" x2="130" y2="110" stroke="#E8DCC8" strokeWidth="1.4"/>
          <line x1="200" y1="110" x2="210" y2="110" stroke="#E8DCC8" strokeWidth="1.4"/>
          <line x1="149" y1="89" x2="142" y2="82" stroke="#E8DCC8" strokeWidth="1.2"/>
          <line x1="191" y1="89" x2="198" y2="82" stroke="#E8DCC8" strokeWidth="1.2"/>
          <line x1="149" y1="131" x2="142" y2="138" stroke="#E8DCC8" strokeWidth="1.2"/>
          <line x1="191" y1="131" x2="198" y2="138" stroke="#E8DCC8" strokeWidth="1.2"/>
        </g>
        <circle cx="170" cy="110" r="4" fill="#fff" opacity="0">
          <animate attributeName="opacity" values="0;0;1;0" dur="3.4s" begin="0s" keyTimes="0;0.77;0.8;0.93" repeatCount="indefinite"/>
        </circle>

        <circle cx="52" cy="94" r="4" fill="rgba(255,255,255,.7)"/>
        <circle cx="270" cy="94" r="4" fill="rgba(255,255,255,.7)"/>
      </svg>

      <p style={{ fontFamily: D.serif, fontWeight: 600, fontSize: 16, color: D.h, margin: "14px 0 4px", textAlign: "center" }}>
        {lang === "ru" ? "Рождается новая связь" : "A new connection is forming"}
      </p>
      <p style={{ fontSize: 12, color: D.ter, margin: "0 0 20px", textAlign: "center" }}>
        {lang === "ru" ? "Пишем историю специально для тебя" : "Writing your story"}
      </p>

      {fact && (
        <div style={{ background: "rgba(255,255,255,.04)", border: `1px solid ${D.borderSoft}`, borderRadius: 14, padding: "14px 16px", width: "100%", maxWidth: 340 }}>
          <p style={{ fontSize: 9, letterSpacing: ".1em", textTransform: "uppercase", color: "#8A6A50", margin: "0 0 6px" }}>
            {lang === "ru" ? "Пока ждёшь" : "While you wait"}
          </p>
          <p style={{ fontSize: 13, color: D.body, lineHeight: 1.5, margin: 0 }}>{fact.fact}</p>
        </div>
      )}
    </div>
  );
}

// ── StyleGateModal: pick a reading style — before track start, or mid-read switch ──
function StyleGateModal({ t, lang, mode, domain, track, currentPreset, onConfirm, onClose }) {
  const [selected, setSelected] = useState(currentPreset || 'popscience');
  const isStart = mode === 'start';

  return (
    <div onClick={e=>e.target===e.currentTarget && onClose && onClose()} style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,.6)", zIndex:1100,
      display:"flex", alignItems:"flex-end", justifyContent:"center",
    }}>
      <div style={{
        background: D.panel, borderRadius:"20px 20px 0 0", padding:"24px 20px 32px",
        width:"100%", maxWidth:480, animation:"wt_slideup .35s ease",
        border:`1px solid ${D.border}`, borderBottom:"none",
        fontFamily: D.ui,
      }}>
        {isStart && domain && track && (
          <div style={{marginBottom:20}}>
            <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:4}}>
              <span style={{fontSize:20}}>{domain.icon}</span>
              <span style={{fontFamily:D.serif, fontSize:15, color:D.h}}>{domain.name[lang]}</span>
            </div>
            <p style={{fontFamily:D.serif, fontSize:20, fontWeight:600, color:D.h, margin:"6px 0 4px"}}>{track.name[lang]}</p>
            <p style={{fontSize:12, color:D.ter, margin:0}}>
              {track.articles.length} {lang==="ru"?"глав":"chapters"} · {track.desc[lang]}
            </p>
          </div>
        )}

        <p style={{fontSize:10, letterSpacing:".12em", textTransform:"uppercase", color:D.ter2, margin:"0 0 12px"}}>
          {isStart
            ? (lang==="ru" ? "В каком стиле читать?" : "Which style?")
            : (lang==="ru" ? "Сменить стиль" : "Change style")}
        </p>

        <div style={{display:"flex", flexDirection:"column", gap:10, marginBottom:20}}>
          {PRESETS.map((p, i) => {
            const active = selected === p.id;
            return (
              <div key={p.id} onClick={() => setSelected(p.id)}
                style={{
                  position:"relative", cursor:"pointer",
                  border:`1px solid ${active ? D.ember : D.border}`,
                  background: active ? "rgba(196,119,74,.1)" : "rgba(255,255,255,.03)",
                  borderRadius:14, padding:"14px 16px",
                }}>
                {active && (
                  <div style={{
                    position:"absolute", top:14, right:16, width:18, height:18, borderRadius:"50%",
                    background:D.ember, display:"flex", alignItems:"center", justifyContent:"center",
                  }}>
                    <span style={{fontSize:11, color:D.bg, lineHeight:1}}>✓</span>
                  </div>
                )}
                <p style={{fontFamily:D.serif, fontSize:15, fontWeight:active?600:400, color: active ? D.h : D.body, margin:"0 0 3px"}}>
                  {t.presets[i]}
                </p>
                <p style={{fontSize:12, color: active ? "#8A6A50" : D.ter, margin:0}}>
                  {t.presetDescs[i]}
                </p>
              </div>
            );
          })}
        </div>

        <button onClick={() => onConfirm(selected)} style={{
          width:"100%", background:D.ember, color:"#fff", border:"none", borderRadius:14,
          padding:14, fontFamily:D.ui, fontSize:15, fontWeight:600, cursor:"pointer",
        }}>
          {isStart
            ? (lang==="ru" ? "Начать · +10 XP →" : "Start · +10 XP →")
            : (lang==="ru" ? "Применить →" : "Apply →")}
        </button>

        {isStart ? (
          <p style={{textAlign:"center", fontSize:11, color:D.ter2, margin:"12px 0 0"}}>
            {lang==="ru" ? "Стиль можно сменить в любой момент чтения" : "You can change the style anytime while reading"}
          </p>
        ) : (
          <button onClick={onClose} style={{
            width:"100%", background:"transparent", border:"none", color:D.ter,
            fontSize:12, cursor:"pointer", fontFamily:D.ui, marginTop:10, padding:8,
          }}>
            {lang==="ru" ? "Отмена" : "Cancel"}
          </button>
        )}
      </div>
    </div>
  );
}

// ── FlashcardsView: full-screen ember cards, tap to reveal ───────────────────
function FlashcardsView({ lang, cards, index, revealed, setRevealed, loading, onAnswer, result, onExit, onShareCard, onBack }) {
  const wrap = {
    position: "fixed", inset: 0, zIndex: 200,
    display: "flex", flexDirection: "column",
    background: `linear-gradient(160deg, ${D.ember} 0%, #A85D38 100%)`,
    color: "#fff", fontFamily: D.ui,
  };

  // Loading state — spinner while generating cards
  if (loading) {
    return (
      <div style={{ ...wrap, alignItems: "center", justifyContent: "center", gap: 16 }}>
        <div style={{
          width: 34, height: 34,
          border: "3px solid rgba(255,255,255,.3)", borderTopColor: "#fff",
          borderRadius: "50%", animation: "wtspin .8s linear infinite",
        }}/>
        <p style={{ fontSize: 14, opacity: .9 }}>
          {lang === "ru" ? "Готовлю карточки…" : "Preparing cards…"}
        </p>
        <style>{`@keyframes wtspin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  // No cards generated (fallback) — skip straight through with a friendly note
  if (!cards.length) {
    return (
      <div style={{ ...wrap, alignItems: "center", justifyContent: "center", padding: "40px 26px", textAlign: "center", gap: 20 }}>
        <p style={{ fontFamily: D.serif, fontSize: 22, lineHeight: 1.4, maxWidth: 420 }}>
          {lang === "ru" ? "На этот раз без карточек — идём к финалу." : "No cards this time — let's wrap up."}
        </p>
        <button onClick={() => onAnswer(false)}
          style={{
            background: "#fff", color: D.ember, border: "none", borderRadius: 26,
            padding: "13px 28px", fontFamily: D.ui, fontSize: 15, fontWeight: 700, cursor: "pointer",
          }}>
          {lang === "ru" ? "Дальше →" : "Continue →"}
        </button>
      </div>
    );
  }

  const card = cards[index];
  const total = cards.length;
  const isTF = card.type === "truefalse";

  // For true/false cards: revealed doubles as "user has answered" and stores which
  // choice they picked, so we can show correct/incorrect feedback before advancing.
  const pickedTF = revealed && isTF ? revealed : null; // "true" | "false" | null
  const tfCorrect = pickedTF ? pickedTF === card.answer : null;

  const handleTFPick = (choice) => {
    setRevealed(choice); // store the picked value; treated as truthy to reveal feedback
  };

  return (
    <div style={wrap}>
      {/* Exit button */}
      <button onClick={onExit} aria-label={lang === "ru" ? "Выйти" : "Exit"}
        style={{
          position: "fixed", top: 14, right: 14, zIndex: 220,
          width: 36, height: 36, borderRadius: "50%",
          background: "rgba(255,255,255,.14)", border: "1px solid rgba(255,255,255,.28)",
          color: "#fff", cursor: "pointer", fontSize: 18, lineHeight: 1,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>×</button>

      {/* Back to previous card */}
      {index > 0 && onBack && (
        <button onClick={onBack} aria-label={lang === "ru" ? "Назад" : "Back"}
          style={{
            position: "fixed", top: 14, left: 14, zIndex: 220,
            width: 36, height: 36, borderRadius: "50%",
            background: "rgba(255,255,255,.14)", border: "1px solid rgba(255,255,255,.28)",
            color: "#fff", cursor: "pointer", fontSize: 18, lineHeight: 1,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>←</button>
      )}

      {/* Progress dots */}
      <div style={{ display: "flex", gap: 6, justifyContent: "center", padding: "18px 16px 4px" }}>
        {cards.map((_, i) => (
          <div key={i} style={{
            height: 3, borderRadius: 2, flex: 1, maxWidth: 50,
            background: i < index ? "rgba(255,255,255,.9)" : i === index ? "#fff" : "rgba(255,255,255,.28)",
          }}/>
        ))}
      </div>

      {/* Card body */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column", justifyContent: "center",
        maxWidth: 560, margin: "0 auto", padding: "20px 26px 40px", width: "100%", textAlign: "center",
      }}>
        <p style={{
          fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase",
          opacity: .7, margin: "0 0 10px",
        }}>
          {lang === "ru" ? "Карточка" : "Card"} {index + 1}/{total}
          {isTF ? (lang === "ru" ? " · Правда или ложь" : " · True or false") : ""}
        </p>

        <div style={{
          fontFamily: D.serif, fontSize: 26, lineHeight: 1.45, fontWeight: 500,
          minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {card.question}
        </div>

        {isTF ? (
          <div style={{ marginTop: 20, minHeight: 60 }}>
            {pickedTF ? (
              <div style={{
                fontFamily: D.serif, fontSize: 22, fontWeight: 600,
                background: tfCorrect ? "rgba(255,255,255,.22)" : "rgba(0,0,0,.22)",
                borderRadius: 14, padding: "14px 22px", display: "inline-block",
              }}>
                {tfCorrect
                  ? (lang === "ru" ? "✓ Верно!" : "✓ Correct!")
                  : (lang === "ru" ? `✗ На самом деле — ${card.answer === "true" ? "правда" : "ложь"}` : `✗ Actually — ${card.answer === "true" ? "true" : "false"}`)}
              </div>
            ) : (
              <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <button onClick={() => handleTFPick("true")}
                  style={{
                    background: "rgba(255,255,255,.14)", color: "#fff",
                    border: "1px solid rgba(255,255,255,.35)", borderRadius: 26,
                    padding: "13px 28px", fontFamily: D.ui, fontSize: 15, fontWeight: 600, cursor: "pointer",
                  }}>
                  {lang === "ru" ? "Правда" : "True"}
                </button>
                <button onClick={() => handleTFPick("false")}
                  style={{
                    background: "rgba(255,255,255,.14)", color: "#fff",
                    border: "1px solid rgba(255,255,255,.35)", borderRadius: 26,
                    padding: "13px 28px", fontFamily: D.ui, fontSize: 15, fontWeight: 600, cursor: "pointer",
                  }}>
                  {lang === "ru" ? "Ложь" : "False"}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: 20, minHeight: 60 }}>
            {revealed ? (
              <div style={{
                fontFamily: D.serif, fontSize: 24, fontWeight: 600,
                background: "rgba(255,255,255,.16)", borderRadius: 14,
                padding: "14px 22px", display: "inline-block",
              }}>
                {card.answer}
              </div>
            ) : (
              <button onClick={() => setRevealed(true)}
                style={{
                  background: "rgba(255,255,255,.14)", color: "#fff",
                  border: "1px solid rgba(255,255,255,.35)", borderRadius: 26,
                  padding: "13px 28px", fontFamily: D.ui, fontSize: 15, fontWeight: 600, cursor: "pointer",
                }}>
                {lang === "ru" ? "Показать ответ" : "Tap to reveal"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Memory Shield tag */}
      <div style={{ textAlign: "center", padding: "0 20px 10px", fontSize: 12, opacity: .8 }}>
        🛡 Memory Shield · {result?.title}
      </div>

      {/* Continue button — appears once answered, wording differs by card type */}
      {isTF && pickedTF && (
        <div style={{ maxWidth: 560, margin: "0 auto", width: "100%", padding: "0 26px 34px", display: "flex", flexDirection: "column", gap: 10 }}>
          <button onClick={() => onAnswer(tfCorrect)}
            style={{
              width: "100%", background: "#fff", color: D.ember,
              border: "none", borderRadius: 14, padding: "15px",
              fontFamily: D.ui, fontSize: 15, fontWeight: 700, cursor: "pointer",
            }}>
            {lang === "ru" ? "Дальше →" : "Continue →"}
          </button>
          {onShareCard && (
            <button onClick={() => onShareCard(card)}
              style={{
                width: "100%", background: "transparent", color: "#fff",
                border: "1px solid rgba(255,255,255,.35)", borderRadius: 14, padding: "12px",
                fontFamily: D.ui, fontSize: 13, fontWeight: 500, cursor: "pointer",
              }}>
              {lang === "ru" ? "Поделиться этим вопросом →" : "Share this question →"}
            </button>
          )}
        </div>
      )}

      {/* Forgot / Got it — only after reveal, blank cards only */}
      {!isTF && revealed && (
        <div style={{
          display: "flex", gap: 12, maxWidth: 560, margin: "0 auto", width: "100%",
          padding: "0 26px 34px",
        }}>
          <button onClick={() => onAnswer(false)}
            style={{
              flex: 1, background: "rgba(0,0,0,.22)", color: "#fff",
              border: "none", borderRadius: 14, padding: "15px",
              fontFamily: D.ui, fontSize: 15, fontWeight: 600, cursor: "pointer",
            }}>
            {lang === "ru" ? "Забыл" : "Forgot"}
          </button>
          <button onClick={() => onAnswer(true)}
            style={{
              flex: 1, background: "#fff", color: D.ember,
              border: "none", borderRadius: 14, padding: "15px",
              fontFamily: D.ui, fontSize: 15, fontWeight: 700, cursor: "pointer",
            }}>
            {lang === "ru" ? "Помню ✓" : "Got it ✓"}
          </button>
        </div>
      )}
    </div>
  );
}


// Picks a random library story (popscience/eli8) for the hook's "Another question"
// button. Same track_stories lookup pattern as handleStartTrack, public-readable
// via the existing "Allow reading library stories" RLS policy (is_library=true).
async function pickRandomHookStory(lang) {
  const pool = [];
  DOMAINS.forEach(domain => {
    domain.tracks.forEach(track => {
      track.articles.forEach((article, idx) => {
        pool.push({ domainId: domain.id, trackId: track.id, articleIndex: idx });
      });
    });
  });
  if (!pool.length) return null;
  const pick = pool[Math.floor(Math.random() * pool.length)];
  const preset = Math.random() < 0.5 ? 'popscience' : 'eli8';
  const { data } = await supabase
    .from('track_stories')
    .select('id, title, story, wiki_url, lang, pull_quote, images, flashcards(type, question, answer, is_hook)')
    .eq('is_library', true)
    .eq('domain_id', pick.domainId)
    .eq('track_id', pick.trackId)
    .eq('article_index', pick.articleIndex)
    .eq('preset', preset)
    .eq('lang', lang)
    .order('engine_version', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!data) return null;
  const prefetchedCard = (data.flashcards || []).find(c => c.is_hook) || (data.flashcards || [])[0] || null;
  return { id: data.id, title: data.title, story: data.story, wiki_url: data.wiki_url, lang: data.lang, pullQuote: data.pull_quote || null, sceneImages: data.images || [], prefetchedCard };
}

// ── HookScreen: fullscreen True/False teaser, no login required ──────────────
// SMM entry point. Same visual grammar as FlashcardsView (terracotta gradient,
// same reveal pattern) so it doesn't feel like a different app once the reader
// taps through into the full story.
function HookScreen({ lang, story, card, loading, answered, onAnswer, onReadFull, onAnother, onClose }) {
  const wrap = {
    position: "fixed", inset: 0, zIndex: 300,
    display: "flex", flexDirection: "column",
    background: `linear-gradient(160deg, ${D.ember} 0%, #A85D38 100%)`,
    color: "#fff", fontFamily: D.ui,
  };

  const closeBtn = (
    <button onClick={onClose} aria-label={lang === "ru" ? "Закрыть" : "Close"}
      style={{
        position: "fixed", top: 14, right: 14, zIndex: 320,
        width: 36, height: 36, borderRadius: "50%",
        background: "rgba(255,255,255,.14)", border: "1px solid rgba(255,255,255,.28)",
        color: "#fff", cursor: "pointer", fontSize: 18, lineHeight: 1,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>×</button>
  );

  if (loading || !card) {
    return (
      <div style={{ ...wrap, alignItems: "center", justifyContent: "center", gap: 16 }}>
        {closeBtn}
        <div style={{
          width: 34, height: 34,
          border: "3px solid rgba(255,255,255,.3)", borderTopColor: "#fff",
          borderRadius: "50%", animation: "wtspin .8s linear infinite",
        }}/>
        <p style={{ fontSize: 14, opacity: .9 }}>
          {lang === "ru" ? "Готовлю вопрос…" : "Preparing a question…"}
        </p>
        <style>{`@keyframes wtspin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  const correct = answered ? answered === card.answer : null;

  return (
    <div style={wrap}>
      {closeBtn}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column", justifyContent: "center",
        maxWidth: 560, margin: "0 auto", padding: "20px 26px 40px", width: "100%", textAlign: "center",
      }}>
        <p style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", opacity: .7, margin: "0 0 10px" }}>
          {lang === "ru" ? "Правда или ложь" : "True or false"}
        </p>

        <div style={{
          fontFamily: D.serif, fontSize: 26, lineHeight: 1.45, fontWeight: 500,
          minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {card.question}
        </div>

        <div style={{ marginTop: 20, minHeight: 60 }}>
          {answered ? (
            <div style={{
              fontFamily: D.serif, fontSize: 22, fontWeight: 600,
              background: correct ? "rgba(255,255,255,.22)" : "rgba(0,0,0,.22)",
              borderRadius: 14, padding: "14px 22px", display: "inline-block",
            }}>
              {correct
                ? (lang === "ru" ? "✓ Верно!" : "✓ Correct!")
                : (lang === "ru" ? `✗ На самом деле — ${card.answer === "true" ? "правда" : "ложь"}` : `✗ Actually — ${card.answer === "true" ? "true" : "false"}`)}
            </div>
          ) : (
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button onClick={() => onAnswer("true")}
                style={{
                  background: "rgba(255,255,255,.14)", color: "#fff",
                  border: "1px solid rgba(255,255,255,.35)", borderRadius: 26,
                  padding: "13px 28px", fontFamily: D.ui, fontSize: 15, fontWeight: 600, cursor: "pointer",
                }}>
                {lang === "ru" ? "Правда" : "True"}
              </button>
              <button onClick={() => onAnswer("false")}
                style={{
                  background: "rgba(255,255,255,.14)", color: "#fff",
                  border: "1px solid rgba(255,255,255,.35)", borderRadius: 26,
                  padding: "13px 28px", fontFamily: D.ui, fontSize: 15, fontWeight: 600, cursor: "pointer",
                }}>
                {lang === "ru" ? "Ложь" : "False"}
              </button>
            </div>
          )}
        </div>
      </div>

      {answered && (
        <div style={{ maxWidth: 560, margin: "0 auto", width: "100%", padding: "0 26px 34px", display: "flex", flexDirection: "column", gap: 10 }}>
          <button onClick={onReadFull}
            style={{
              width: "100%", background: "#fff", color: D.ember,
              border: "none", borderRadius: 14, padding: "15px",
              fontFamily: D.ui, fontSize: 15, fontWeight: 700, cursor: "pointer",
            }}>
            {lang === "ru" ? "Узнать всю историю →" : "Read the full story →"}
          </button>
          <button onClick={onAnother}
            style={{
              width: "100%", background: "transparent", color: "#fff",
              border: "1px solid rgba(255,255,255,.35)", borderRadius: 14, padding: "12px",
              fontFamily: D.ui, fontSize: 13, fontWeight: 500, cursor: "pointer",
            }}>
            {lang === "ru" ? "Другой вопрос →" : "Another question →"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── VictoryView: single lit neuron + stats + next button ─────────────────────
function VictoryView({ lang, result, fcResults, xp, streak, onNext, activeTrack }) {
  const got = fcResults.filter(Boolean).length;
  const total = fcResults.length;
  const hasNext = activeTrack && activeTrack.articleIndex + 1 < activeTrack.track.articles.length;

  const wrap = {
    position: "fixed", inset: 0, background: D.bg, zIndex: 200,
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    color: D.body, fontFamily: D.ui,
    padding: "40px 26px", textAlign: "center",
  };

  const stat = (val, label, color) => (
    <div style={{ flex: 1 }}>
      <div style={{ fontFamily: D.serif, fontSize: 26, fontWeight: 600, color: color || D.h }}>{val}</div>
      <div style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: D.sec, marginTop: 2 }}>{label}</div>
    </div>
  );

  return (
    <div style={wrap}>
      {/* Neuron lighting up */}
      <svg width="120" height="120" viewBox="0 0 120 120" style={{ marginBottom: 20 }}>
        <defs>
          <radialGradient id="wt-soma" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={D.emberBright}/>
            <stop offset="100%" stopColor={D.ember}/>
          </radialGradient>
        </defs>
        {/* Dendrites */}
        {[0, 60, 120, 180, 240, 300].map(a => {
          const r = (a * Math.PI) / 180;
          return (
            <line key={a} x1="60" y1="60"
              x2={60 + Math.cos(r) * 42} y2={60 + Math.sin(r) * 42}
              stroke={D.ember} strokeWidth="1.5" opacity=".5"/>
          );
        })}
        {/* Soma */}
        <circle cx="60" cy="60" r="26" fill="url(#wt-soma)">
          <animate attributeName="r" values="26;29;26" dur="2s" repeatCount="indefinite"/>
        </circle>
        {/* Pulse ring */}
        <circle cx="60" cy="60" r="26" fill="none" stroke={D.emberBright} strokeWidth="1.5" opacity=".6">
          <animate attributeName="r" values="26;46" dur="2s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values=".6;0" dur="2s" repeatCount="indefinite"/>
        </circle>
        {/* Highlight */}
        <circle cx="54" cy="54" r="7" fill="rgba(255,255,255,.85)"/>
      </svg>

      <p style={{
        fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase",
        color: D.ember, margin: "0 0 8px", fontWeight: 600,
      }}>
        {lang === "ru" ? "Нейрон загорелся" : "Neuron lit up"}
      </p>
      <h1 style={{
        fontFamily: D.serif, fontSize: 26, fontWeight: 600, color: D.h,
        margin: "0 0 30px", lineHeight: 1.3, maxWidth: 420,
      }}>
        {lang === "ru" ? "Ты понял" : "You understood"} «{result?.title}»
      </h1>

      {/* Stats row */}
      <div style={{
        display: "flex", gap: 20, width: "100%", maxWidth: 360, marginBottom: 34,
        background: D.panel, border: `1px solid ${D.border}`, borderRadius: 16, padding: "18px 16px",
      }}>
        {stat(`+${xp?.gained ?? 10}`, "XP", D.emberBright)}
        {stat(`🔥 ${streak}`, lang === "ru" ? "Серия" : "Streak", D.h)}
        {total > 0 && stat(`${got}/${total}`, "Recall", got === total ? D.green : D.h)}
      </div>

      <button onClick={onNext}
        style={{
          background: D.ember, color: "#fff", border: "none", borderRadius: 26,
          padding: "15px 34px", fontFamily: D.ui, fontSize: 15, fontWeight: 600, cursor: "pointer",
          boxShadow: "0 6px 20px rgba(196,119,74,.4)",
        }}>
        {hasNext
          ? (lang === "ru" ? "Следующая глава →" : "Next chapter →")
          : (lang === "ru" ? "К прогрессу →" : "To progress →")}
      </button>
    </div>
  );
}

function EmailCapturePrompt({ lang, user, authEmail, setAuthEmail, onMagicLink, onGoogle, magicSent, onSkip, onAlreadyAuthSubscribe }) {
  const isRu = lang === 'ru';
  const wrap = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 250,
    display: "flex", alignItems: "flex-end", justifyContent: "center",
  };
  const sheet = {
    background: D.panel, borderRadius: "20px 20px 0 0", padding: "24px 20px 32px",
    width: "100%", maxWidth: 480, border: `1px solid ${D.border}`, borderBottom: "none",
    fontFamily: D.ui, animation: "wt_slideup .35s ease",
  };

  // Already logged in, just not subscribed — one tap, no auth flow needed
  if (user) {
    return (
      <div style={wrap} onClick={e => e.target === e.currentTarget && onSkip()}>
        <div style={sheet}>
          <p style={{ fontFamily: D.serif, fontSize: 18, color: D.h, margin: "0 0 4px" }}>
            {isRu ? "Получай историю дня на почту" : "Get tomorrow's story by email"}
          </p>
          <p style={{ fontSize: 12, color: D.sec, margin: "0 0 20px" }}>
            {isRu ? "Одно письмо в день — не пропустишь серию" : "One email a day — never miss your streak"}
          </p>
          <button onClick={onAlreadyAuthSubscribe} style={{
            width: "100%", padding: 12, background: D.ember, color: "#fff", border: "none",
            borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 8,
          }}>
            {isRu ? "Подписаться" : "Subscribe"}
          </button>
          <button onClick={onSkip} style={{ width: "100%", padding: 8, background: "transparent", border: "none", color: D.ter, fontSize: 12, cursor: "pointer" }}>
            {isRu ? "Не сейчас" : "Not now"}
          </button>
        </div>
      </div>
    );
  }

  // Anonymous — reuse the existing auth flow (Google + magic link), framed around saving progress
  return (
    <div style={wrap} onClick={e => e.target === e.currentTarget && onSkip()}>
      <div style={sheet}>
        <p style={{ fontFamily: D.serif, fontSize: 18, color: D.h, margin: "0 0 4px" }}>
          {isRu ? "Сохрани серию" : "Save your streak"}
        </p>
        <p style={{ fontSize: 12, color: D.sec, margin: "0 0 20px" }}>
          {isRu ? "Войди — и мы будем присылать историю дня на почту" : "Sign in and we'll email you tomorrow's story"}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          <button onClick={onGoogle} style={{ width: "100%", padding: 11, background: D.panel2, border: `1px solid ${D.border}`, borderRadius: 12, fontSize: 13, cursor: "pointer", color: D.h }}>
            G · {isRu ? "Войти через Google" : "Continue with Google"}
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <input type="email" placeholder="your@email.com" value={authEmail} onChange={e => setAuthEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && onMagicLink()}
              style={{ flex: 1, background: D.panel3, border: `1px solid ${D.border}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: D.h, outline: "none" }} />
            <button onClick={onMagicLink} style={{ background: D.ember, color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontSize: 13, cursor: "pointer", fontFamily: D.serif }}>→</button>
          </div>
          {magicSent && (
            <p style={{ fontSize: 12, color: D.emberBright, textAlign: "center", margin: 0 }}>
              {isRu ? "✉️ Проверь почту" : "✉️ Check your inbox"}
            </p>
          )}
        </div>
        <button onClick={onSkip} style={{ width: "100%", padding: 8, background: "transparent", border: "none", color: D.ter, fontSize: 12, cursor: "pointer" }}>
          {isRu ? "Не сейчас" : "Not now"}
        </button>
      </div>
    </div>
  );
}

function InstallPrompt({ lang, onAccept, onDismiss }) {
  const isRu = lang === 'ru';
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  const wrap = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 250,
    display: "flex", alignItems: "flex-end", justifyContent: "center",
  };
  const sheet = {
    background: D.panel, borderRadius: "20px 20px 0 0", padding: "24px 20px 32px",
    width: "100%", maxWidth: 480, border: `1px solid ${D.border}`, borderBottom: "none",
    fontFamily: D.ui, animation: "wt_slideup .35s ease",
  };

  return (
    <div style={wrap} onClick={e => e.target === e.currentTarget && onDismiss()}>
      <div style={sheet}>
        <p style={{ fontFamily: D.serif, fontSize: 18, color: D.h, margin: "0 0 4px" }}>
          {isRu ? "Добавь WikiTale на экран" : "Add WikiTale to your Home Screen"}
        </p>
        <p style={{ fontSize: 12, color: D.sec, margin: "0 0 20px" }}>
          {isRu ? "Чтобы серия не сгорела" : "So your streak doesn't burn out"}
        </p>

        {isIOS ? (
          <div style={{
            display: "flex", flexDirection: "column", gap: 10, marginBottom: 16,
            background: D.panel3, border: `1px solid ${D.border}`, borderRadius: 12, padding: 14,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: D.body }}>
              <span style={{ fontSize: 16 }}>􀈂</span>
              {isRu ? "Нажми «Поделиться» внизу экрана" : "Tap the Share icon at the bottom"}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: D.body }}>
              <span style={{ fontSize: 16 }}>➕</span>
              {isRu ? "Выбери «На экран Домой»" : 'Choose "Add to Home Screen"'}
            </div>
          </div>
        ) : null}

        {!isIOS && (
          <button onClick={onAccept} style={{
            width: "100%", padding: 12, background: D.ember, color: "#fff", border: "none",
            borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: 8,
          }}>
            {isRu ? "Установить" : "Install"}
          </button>
        )}
        <button onClick={onDismiss} style={{ width: "100%", padding: 8, background: "transparent", border: "none", color: D.ter, fontSize: 12, cursor: "pointer" }}>
          {isRu ? "Позже" : "Later"}
        </button>
      </div>
    </div>
  );
}

function CreateScreen({
  t, lang, input, setInput, length, setLength, tone, setTone, style, setStyle,
  activePreset, setActivePreset, styleOpen, setStyleOpen,
  phase, result, facts, storyReady, errorMsg, copied, reaction,
  activeTrack, setActiveTrack,
  onRun, onRead, onPick, onBack, onRegen, onReaction, onShare, onCopy, onNextInTrack, onJumpToTrack,
  disambigResults, disambigQuery, user, authEmail, setAuthEmail, magicSent, onMagicLink, onGoogle, onSignOut,
  showFeedback, setShowFeedback,
}) {
  const [focused, setFocused] = useState(false);
  const isLoading = phase === "loading" || phase === "searching";

  const mkBtn = (v="primary") => ({
    padding:v==="primary"?"12px 22px":"10px 16px", minHeight:"44px",
    fontSize:v==="primary"?"13px":"12px", fontFamily:D.ui,
    fontWeight:v==="primary"?"700":"400", letterSpacing:"0.04em", borderRadius:"12px", cursor:"pointer",
    border:v==="primary"?"none":`1px solid ${D.border}`,
    background:v==="primary"?D.ember:"transparent",
    color:v==="primary"?"#fff":D.sec,
    opacity:isLoading&&v==="primary"?0.55:1, whiteSpace:"nowrap",
  });

  const card = {background:D.panel,border:`1px solid ${D.border}`,borderRadius:"16px",padding:"20px",marginBottom:"14px"};
  const lbl = {fontSize:"10px",letterSpacing:"0.14em",textTransform:"uppercase",color:D.ter,marginBottom:"12px",fontFamily:D.ui};

  return (
    <div style={{
      background: D.bg, color: D.body, fontFamily: D.ui,
      minHeight:"calc(100vh - 80px)",
      paddingTop:"20px", paddingBottom:"20px",
    }}>
    <div style={{maxWidth:"620px",margin:"0 auto",padding:"0 16px",width:"100%"}}>

      <TrackNavigator
        t={t} lang={lang} activeTrack={activeTrack} setActiveTrack={setActiveTrack}
        onJumpTo={onJumpToTrack} onChangeStyle={()=>{ setActivePreset(null); setStyleOpen(true); }}
        styleLabel={`${activePreset ? t.presets[PRESETS.findIndex(p=>p.id===activePreset)] : t.toneLabels[tone-1]} · ${t.lengthHints[length-1]}`}
      />

      <div style={card}>
        <p style={lbl}>{lang==="ru"?"Тема или ссылка на Википедию":"Topic or Wikipedia link"}</p>
        <div style={{display:"flex",gap:"10px",flexWrap:"wrap"}}>
          <input
            style={{flex:"1 1 200px",background:D.panel3,border:`1px solid ${D.border}`,borderRadius:"12px",color:D.h,padding:"10px 14px",fontSize:"16px",fontFamily:D.serif,outline:"none",minWidth:0}}
            type="text" placeholder={focused?"":t.inputPlaceholder} value={input}
            onChange={e=>setInput(e.target.value)}
            onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)}
            onKeyDown={e=>e.key==="Enter"&&!isLoading&&onRun()}
            autoFocus
          />
          <button style={mkBtn("primary")} disabled={isLoading||phase==="disambig"} onClick={()=>onRun()}>{t.generate}</button>
        </div>
        {!input && (
          <div style={{display:"flex",gap:"8px",flexWrap:"wrap",marginTop:"12px",alignItems:"center"}}>
            <span style={{fontSize:"11px",color:D.ter,fontFamily:D.ui,whiteSpace:"nowrap"}}>
              {lang==="ru"?"Попробовать:":"Try:"}
            </span>
            {(lang==="ru"?["Наполеон","Клеопатра","Чёрные дыры","Тесла"]:["Napoleon","Black holes","Cleopatra","Tesla"]).map(ex=>(
              <button key={ex} onClick={()=>setInput(ex)} style={{background:"rgba(196,119,74,.12)",color:D.emberBright,border:`1px solid ${D.border}`,borderRadius:"20px",padding:"5px 12px",fontSize:"12px",cursor:"pointer",fontFamily:D.ui}}>{ex}</button>
            ))}
          </div>
        )}
        <p style={{margin:"10px 0 0",fontSize:"11px",color:D.ter,fontFamily:D.ui}}>{t.inputHint}</p>
      </div>

      <div style={{...card,padding:styleOpen?"20px":"14px 20px"}}>
        <div onClick={()=>setStyleOpen(o=>!o)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",userSelect:"none"}}>
          <p style={{...lbl,marginBottom:0}}>{t.styleLabel}</p>
          <span style={{fontSize:"14px",color:D.ter,transition:"transform .25s",display:"inline-block",transform:styleOpen?"rotate(0deg)":"rotate(-90deg)"}}>▾</span>
        </div>
        {styleOpen && (
          <div style={{marginTop:"16px"}}>
            <div style={{display:"flex",flexDirection:"column",gap:"8px",marginBottom:"18px"}}>
              {PRESETS.map((p,i)=>(
                <button key={p.id} onClick={()=>{ setActivePreset(p.id); setLength(p.length); setTone(p.tone); setStyle(p.style); }} style={{
                  padding:"10px 14px",fontSize:"13px",borderRadius:"10px",cursor:"pointer",
                  border:`1px solid ${activePreset===p.id?D.ember:D.border}`,
                  background:activePreset===p.id?"rgba(196,119,74,.12)":"transparent",
                  color:activePreset===p.id?D.emberBright:D.h,
                  fontFamily:D.serif,
                  display:"flex",justifyContent:"space-between",alignItems:"center",
                  textAlign:"left",
                }}>
                  <span>{t.presets[i]}</span>
                  <span style={{fontSize:"11px",color:activePreset===p.id?D.emberBright:D.ter,fontFamily:D.ui}}>{t.presetDescs[i]}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {phase==="disambig" && (
        <div style={card}>
          <p style={{fontSize:"16px",color:D.h,margin:"0 0 16px",fontFamily:D.serif}}>
            {t.disambigQ} <span style={{color:D.emberBright,fontStyle:"italic"}}>"{disambigQuery}"</span> {t.disambigQ2}
          </p>
          <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
            {disambigResults.map((r,i)=>(
              <div key={i} onClick={()=>onPick(r)}
                style={{background:D.panel2,border:`1px solid ${D.border}`,borderRadius:"14px",padding:"14px 16px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"12px"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=D.ember;e.currentTarget.style.background=D.panel3;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=D.border;e.currentTarget.style.background=D.panel2;}}
              >
                <div>
                  <p style={{fontSize:"14px",color:D.h,margin:"0 0 3px",fontFamily:D.serif}}>{r.title}</p>
                  {r.description && <p style={{fontSize:"12px",color:D.sec,lineHeight:"1.5",margin:0,fontFamily:D.ui}}>{r.description}</p>}
                </div>
                <span style={{color:D.emberBright,fontSize:"16px",flexShrink:0}}>→</span>
              </div>
            ))}
          </div>
          <button onClick={onBack} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:"12px",color:D.ter,fontFamily:D.ui,marginTop:"12px",padding:0}}>{t.changeQuery}</button>
        </div>
      )}

      {phase==="error" && (
        <div style={{background:D.redBg,border:`1px solid rgba(155,74,74,.4)`,borderRadius:"16px",padding:"14px 18px",color:"#E08A8A",fontSize:"13px",marginBottom:"14px"}}>{errorMsg}</div>
      )}

      {phase==="loading" && (
        <div style={{padding:"8px 0"}}>
          <GrowingNeuronLoader lang={lang} fact={facts?.[facts.length - 1]} />
        </div>
      )}

      {phase==="paywall" && (
        <div style={{...card,textAlign:"center",padding:"32px 24px"}}>
          <div style={{fontSize:"40px",marginBottom:"12px"}}>📚</div>
          <h2 style={{fontFamily:D.serif,color:D.h,fontWeight:"600",fontSize:"22px",margin:"0 0 8px"}}>
            {lang==="ru"?"5 историй использовано":"5 stories used"}
          </h2>
          <p style={{fontSize:"13px",color:D.sec,fontFamily:D.ui,margin:"0 0 20px",lineHeight:1.7}}>
            {lang==="ru"?"Войди чтобы сохранить прогресс и продолжить читать.":"Sign in to save your progress and keep reading."}
          </p>
          {!user && (
            <div style={{marginBottom:"16px"}}>
              {magicSent ? (
                <div style={{background:D.panel2,border:`1px solid ${D.border}`,borderRadius:"12px",padding:"14px",fontSize:"13px",color:D.sec,fontFamily:D.ui}}>{t.magicLinkSent}</div>
              ) : (
                <>
                  <div style={{display:"flex",gap:"8px",marginBottom:"8px"}}>
                    <input type="email" placeholder={t.emailPlaceholder} value={authEmail} onChange={e=>setAuthEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&onMagicLink()}
                      style={{flex:1,background:D.panel3,border:`1px solid ${D.border}`,borderRadius:"10px",padding:"10px 14px",fontSize:"13px",color:D.h,fontFamily:D.ui,outline:"none"}}/>
                    <button onClick={onMagicLink} style={{background:D.ember,color:"#fff",border:"none",borderRadius:"10px",padding:"10px 18px",fontSize:"13px",cursor:"pointer",fontFamily:D.serif}}>→</button>
                  </div>
                  <button onClick={onGoogle} style={{width:"100%",padding:"10px 16px",minHeight:"44px",fontSize:"12px",fontFamily:D.serif,borderRadius:"12px",cursor:"pointer",border:`1px solid ${D.border}`,background:"transparent",color:D.sec,marginBottom:"4px"}}>G · {t.orGoogle}</button>
                </>
              )}
            </div>
          )}
          <button style={{width:"100%",padding:"14px",background:D.ember,color:"#fff",border:"none",borderRadius:"12px",fontSize:"15px",fontFamily:D.serif,cursor:"pointer",marginBottom:"10px"}}
            onClick={()=>window.open('https://wikitale.lemonsqueezy.com/checkout/buy/dfc1aff7-eb33-4ad5-b852-a808cd73a06d','_blank')}>
            {lang==="ru"?"Оформить подписку €5/мес →":"Subscribe €5/mo →"}
          </button>
          <button style={{width:"100%",padding:"10px 16px",minHeight:"44px",fontSize:"12px",fontFamily:D.serif,borderRadius:"12px",cursor:"pointer",border:`1px solid ${D.border}`,background:"transparent",color:D.sec}} onClick={()=>{}}>
            {lang==="ru"?"← Вернуться":"← Go back"}
          </button>
        </div>
      )}
    </div>
    </div>
  );
}

/// ── Game Screen (Neural Hub) ───────────────────────────────────────────────────

function GameScreen({ t, lang, user, trackProgress, xp, level, streak, weeklyGoal, onSaveWeeklyGoal, onStartTrack, onContinueTrack, onOpenDailyStory, onOpenHistoryArticle }) {
  const [expandedDomainId, setExpandedDomainId] = useState(null);

  const LEVELS = [
    { level: 1, name: lang==="ru"?"Любопытный":"Curious", min: 0 },
    { level: 2, name: lang==="ru"?"Эрудит":"Scholar",     min: 50 },
    { level: 3, name: lang==="ru"?"Мыслитель":"Thinker",  min: 150 },
    { level: 4, name: lang==="ru"?"Мудрец":"Sage",        min: 350 },
  ];
  const currentLevel = LEVELS.find(l => l.level === level) || LEVELS[0];
  const nextLevel = LEVELS.find(l => l.level === level + 1);
  const xpPct = nextLevel
    ? Math.round(((xp - currentLevel.min) / (nextLevel.min - currentLevel.min)) * 100)
    : 100;

  // Find the current "up next" article: the most-advanced unfinished track,
  // or the first untouched track if everything in-progress is complete.
  function findCurrentArticle() {
    let bestDomain=null,bestTrack=null,bestProg=-1;
    DOMAINS.forEach(domain=>{domain.tracks.forEach(track=>{
      const prog=trackProgress[`${domain.id}_${track.id}`]||0;
      const isComplete = prog >= track.articles.length;
      if(!isComplete && prog>bestProg){bestProg=prog;bestDomain=domain;bestTrack=track;}
    });});
    if(!bestTrack){
      outer:
      for(const domain of DOMAINS){for(const track of domain.tracks){
        const prog=trackProgress[`${domain.id}_${track.id}`]||0;
        if(prog===0){bestDomain=domain;bestTrack=track;bestProg=0;break outer;}
      }}
    }
    if(!bestTrack){bestDomain=DOMAINS[0];bestTrack=DOMAINS[0].tracks[0];bestProg=0;}
    const article = bestTrack.articles[bestProg];
    return { domain: bestDomain, track: bestTrack, articleIndex: bestProg, article, isNew: bestProg === 0 };
  }

  const current = findCurrentArticle();

  const totalStories = Object.values(trackProgress).reduce((s,v)=>s+v,0);
  const activeTracks = [];
  const completedTracks = [];
  DOMAINS.forEach(domain=>{domain.tracks.forEach(track=>{const prog=trackProgress[`${domain.id}_${track.id}`]||0;if(prog>0&&prog<track.articles.length)activeTracks.push({domain,track,prog});if(prog>=track.articles.length)completedTracks.push({domain,track});});});

  const handleAction = () => {
    onContinueTrack(current.domain, current.track, current.articleIndex);
  };

  const actionLabel = current.isNew
    ? (lang==="ru"?'Начать · +10 XP →':'Start · +10 XP →')
    : (lang==="ru"?'Продолжить →':'Continue →');

  // Streak calendar: current month, last `streak` days (including today) filled in.
  const today = new Date();
  const year = today.getFullYear(), month = today.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0
  const todayDate = today.getDate();
  const monthNames = lang==="ru"
    ? ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"]
    : ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const weekdayLabels = lang==="ru" ? ["П","В","С","Ч","П","С","В"] : ["M","T","W","T","F","S","S"];
  const calendarCells = [];
  for (let i = 0; i < firstWeekday; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  return (
    <div style={{
      background: D.bg, color: D.body, fontFamily: D.ui,
      minHeight:"calc(100vh - 80px)",
      paddingTop:"20px", paddingBottom:"20px",
    }}>
    <div style={{maxWidth:"620px",margin:"0 auto",padding:"0 16px 32px",width:"100%"}}>

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"10px"}}>
        <div>
          <p style={{fontFamily:D.serif,fontSize:"18px",fontWeight:600,color:D.h,margin:"0 0 2px"}}>
            {lang==="ru"?"Прогресс":"Progress"}
          </p>
          <p style={{fontSize:"12px",color:D.ter,margin:0,fontFamily:D.ui}}>
            {currentLevel.name} · {xp} XP
          </p>
        </div>
        {streak > 0 && (
          <div style={{background:"rgba(196,119,74,.14)",border:"1px solid rgba(196,119,74,.3)",borderRadius:"12px",padding:"8px 14px",display:"flex",alignItems:"center",gap:"6px"}}>
            <span style={{fontSize:"18px"}}>🔥</span>
            <div>
              <div style={{fontFamily:D.serif,fontSize:"16px",color:D.emberBright,lineHeight:1}}>{streak}</div>
              <div style={{fontSize:"9px",color:D.ter,fontFamily:D.ui}}>{lang==="ru"?"дней":"days"}</div>
            </div>
          </div>
        )}
      </div>

      {/* XP bar */}
      <div style={{height:"3px",background:"rgba(255,255,255,.08)",borderRadius:"2px",overflow:"hidden",marginBottom:"20px"}}>
        <div style={{height:"100%",background:`linear-gradient(90deg,${D.ember},${D.emberBright})`,width:`${xpPct}%`,borderRadius:"2px",transition:"width .4s ease"}}/>
      </div>

      {/* Metric cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3, minmax(0, 1fr))",gap:"8px",marginBottom:"16px"}}>
        {[
          {num:totalStories, label:lang==="ru"?"историй":"stories", color:D.h},
          {num:activeTracks.length, label:lang==="ru"?"активных":"active", color:D.h},
          {num:completedTracks.length, label:lang==="ru"?"завершено":"done", color:D.read},
        ].map((s,i)=>(
          <div key={i} style={{background:"rgba(255,255,255,.03)",border:`1px solid ${D.borderSoft}`,borderRadius:"10px",padding:"12px 8px",textAlign:"center"}}>
            <div style={{fontFamily:D.serif,fontSize:"22px",color:s.color,lineHeight:1,marginBottom:"3px"}}>{s.num}</div>
            <div style={{fontSize:"9px",color:D.ter,fontFamily:D.ui}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Streak calendar */}
      <div style={{background:"rgba(255,255,255,.03)",border:`1px solid ${D.borderSoft}`,borderRadius:"14px",padding:"16px",marginBottom:"16px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"14px"}}>
          <span style={{fontSize:"10px",letterSpacing:"0.1em",textTransform:"uppercase",color:D.ter}}>{monthNames[month]} {year}</span>
          {streak > 0 && <span style={{fontSize:"11px",color:"#8A6A50"}}>{lang==="ru"?"серия":"streak"} · {streak} {lang==="ru"?"дней":"days"}</span>}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:"4px",marginBottom:"8px",maxWidth:280,margin:"0 auto 8px"}}>
          {weekdayLabels.map((w,i)=>(
            <span key={i} style={{fontSize:"9px",color:D.ter2,textAlign:"center"}}>{w}</span>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:"4px",maxWidth:280,margin:"0 auto"}}>
          {calendarCells.map((d, i) => {
            if (d === null) return <div key={i}/>;
            const daysAgo = todayDate - d;
            const isStreakDay = daysAgo >= 0 && daysAgo < streak;
            const isToday = d === todayDate;
            const intensity = isStreakDay ? Math.max(0.3, 1 - daysAgo * 0.15) : 0;
            return (
              <div key={i} style={{
                aspectRatio:"1", maxWidth:34, maxHeight:34, margin:"0 auto",
                width:"100%", display:"flex", alignItems:"center", justifyContent:"center",
                fontSize: isToday ? "11px" : "10px", fontWeight: isToday ? 600 : 400,
                borderRadius:"6px",
                background: isToday ? D.emberBright : isStreakDay ? `rgba(196,119,74,${intensity})` : "transparent",
                color: isToday ? D.bg : isStreakDay ? "#F0B888" : D.sec2,
              }}>{d}</div>
            );
          })}
        </div>
      </div>

      {/* Up next */}
      <div style={{background:D.panel2,border:`1px solid ${D.border}`,borderLeft:`3px solid ${D.ember}`,borderRadius:"14px",padding:"14px 16px",marginBottom:"16px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"4px"}}>
          <span style={{fontSize:"10px",letterSpacing:"0.08em",fontFamily:D.ui,fontWeight:"600",color:D.ember}}>
            {current.isNew ? (lang==="ru"?'СЛЕДУЮЩАЯ':'UP NEXT') : (lang==="ru"?'ЧИТАЕШЬ':'READING NOW')}
          </span>
          <span style={{fontSize:"11px",color:D.ter,fontFamily:D.ui}}>{current.domain.name[lang]} · {current.articleIndex+1}/{current.track.articles.length}</span>
        </div>
        <p style={{fontFamily:D.serif,fontSize:"15px",color:D.h,margin:"0 0 10px"}}>{current.article?.[lang] || current.article?.en}</p>
        <button onClick={handleAction} style={{
          width:"100%", border:"none", cursor:"pointer",
          borderRadius:"10px", padding:"10px 0",
          fontSize:"13px", fontFamily:D.serif, fontWeight:"700",
          background: D.ember, color: "#fff",
        }}>
          {actionLabel}
        </button>
      </div>

      <div style={{height:"1px",background:D.borderSoft,margin:"0 0 16px"}}/>

      {/* Progress by domain — expandable list of read articles */}
      <p style={{fontSize:"10px",letterSpacing:"0.14em",textTransform:"uppercase",color:D.ter2,margin:"0 0 10px",fontFamily:D.ui}}>
        {lang==="ru"?"Пройденные истории":"Stories you've read"}
      </p>
      <div style={{display:"flex",flexDirection:"column",gap:"8px",marginBottom:"20px"}}>
        {DOMAINS.map(domain => {
          const domainTracks = domain.tracks
            .map(track => ({ track, prog: trackProgress[`${domain.id}_${track.id}`] || 0 }))
            .filter(({ prog }) => prog > 0);
          if (!domainTracks.length) return null;
          const domainRead = domainTracks.reduce((s, { prog }) => s + prog, 0);
          const domainTotal = domainTracks.reduce((s, { track }) => s + track.articles.length, 0);
          const isOpen = expandedDomainId === domain.id;
          return (
            <div key={domain.id} style={{background:"rgba(255,255,255,.03)",border:`1px solid ${D.borderSoft}`,borderRadius:"12px",overflow:"hidden"}}>
              <div onClick={() => setExpandedDomainId(isOpen ? null : domain.id)}
                style={{padding:"14px 16px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"12px"}}
              >
                <div style={{display:"flex",alignItems:"center",gap:"10px",minWidth:0}}>
                  <span style={{fontSize:"16px",flexShrink:0}}>{domain.icon}</span>
                  <div style={{minWidth:0}}>
                    <p style={{fontFamily:D.serif,fontSize:"14px",color:D.h,margin:"0 0 2px"}}>{domain.name[lang]}</p>
                    <p style={{fontSize:"11px",color:D.ter,margin:0,fontFamily:D.ui}}>{domainRead}/{domainTotal} {lang==="ru"?"глав":"chapters"}</p>
                  </div>
                </div>
                <span style={{fontSize:"12px",color:D.ter,transition:"transform .2s",transform:isOpen?"rotate(180deg)":"none",flexShrink:0}}>▾</span>
              </div>
              {isOpen && (
                <div style={{padding:"0 16px 14px",display:"flex",flexDirection:"column",gap:"6px"}}>
                  {domainTracks.map(({ track, prog }) => (
                    <div key={track.id}>
                      <p style={{fontSize:"10px",color:D.ter2,margin:"8px 0 6px",fontFamily:D.ui,letterSpacing:".04em"}}>{track.name[lang]}</p>
                      {track.articles.slice(0, prog).map((article, i) => (
                        <div key={i}
                          onClick={() => onOpenHistoryArticle?.(domain, track, i)}
                          style={{display:"flex",alignItems:"center",gap:"10px",padding:"8px 10px",borderRadius:"8px",cursor:"pointer"}}
                          onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.05)"}
                          onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                        >
                          <span style={{fontSize:"12px",color:domain.color,flexShrink:0}}>✓</span>
                          <span style={{fontSize:"13px",fontFamily:D.serif,color:D.body,flex:1}}>{article[lang]}</span>
                          <span style={{fontSize:"12px",color:D.ter}}>→</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {activeTracks.length === 0 && completedTracks.length === 0 && (
          <p style={{fontSize:"13px",color:D.ter,fontFamily:D.ui,textAlign:"center",padding:"20px 0"}}>
            {lang==="ru"?"Пока пусто — начни первое путешествие":"Nothing yet — start your first journey"}
          </p>
        )}
      </div>

    </div>
    </div>
  );
}


// ── Explore Screen ─────────────────────────────────────────────────────────────

function ExploreScreen({ t, lang, onStartTrack, trackProgress }) {
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const closePanel = () => {
    setPanelOpen(false);
    setTimeout(() => { setSelectedDomain(null); setSelectedTrack(null); }, 350);
  };

  const getProgress = (domainId, trackId) => trackProgress?.[`${domainId}_${trackId}`] || 0;

  return (
    <div style={{
      background: D.bg, color: D.body, fontFamily: D.ui,
      minHeight:"calc(100vh - 80px)",
      paddingBottom:"20px",
    }}>
      <div style={{padding:"20px 16px 12px", maxWidth:"620px", margin:"0 auto"}}>
        <h2 style={{fontFamily:D.serif,fontSize:"22px",fontWeight:"600",color:D.h,marginBottom:"4px"}}>
          {lang==="ru"?"Исследовать":lang==="de"?"Entdecken":"Explore"}
        </h2>
        <p style={{fontSize:"12px",color:D.ter,fontFamily:D.ui}}>
          {lang==="ru"?"Выбери тему — мы подберём серию историй":lang==="de"?"Wähle ein Thema":"Choose a topic — we'll build a story series for you"}
        </p>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px",padding:"0 16px",maxWidth:"620px",margin:"0 auto"}}>
        {DOMAINS.map(domain => {
          const totalArticles = domain.tracks.reduce((s,tr) => s + tr.articles.length, 0);
          const doneArticles = domain.tracks.reduce((s,tr) => s + getProgress(domain.id, tr.id), 0);
          const pct = totalArticles > 0 ? Math.round((doneArticles / totalArticles) * 100) : 0;
          return (
            <div key={domain.id}
              onClick={() => { setSelectedDomain(domain); setSelectedTrack(null); setPanelOpen(true); }}
              style={{background:doneArticles>0?domain.color+"14":"rgba(255,255,255,.03)",border:"1px solid "+D.borderSoft,borderRadius:"16px",padding:"16px",cursor:"pointer",transition:"all .2s",borderTop:"3px solid "+domain.color}}
              onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,.4)";}}
              onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none";}}
            >
              <div style={{fontSize:"28px",marginBottom:"10px"}}>{domain.icon}</div>
              <div style={{fontFamily:D.serif,fontSize:"17px",color:D.h,marginBottom:"4px"}}>{domain.name[lang]}</div>
              <div style={{fontSize:"10px",color:D.ter,fontFamily:D.ui,marginBottom:"10px"}}>
                {domain.tracks.length} {lang==="ru"?"трека":lang==="de"?"Tracks":"tracks"} · {totalArticles} {t.articles}
              </div>
              <div style={{height:"2px",background:D.border,borderRadius:"1px",overflow:"hidden"}}>
                <div style={{height:"100%",background:domain.color,width:pct+"%",borderRadius:"1px",transition:"width .3s"}}/>
              </div>
              {doneArticles > 0 && (
                <div style={{fontSize:"10px",color:domain.color,fontFamily:D.ui,marginTop:"5px"}}>
                  {doneArticles}/{totalArticles} {t.trackProgress}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div onClick={closePanel} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:15,opacity:panelOpen?1:0,pointerEvents:panelOpen?"all":"none",transition:"opacity .3s"}}/>
      <div style={{position:"fixed",bottom:"57px",left:0,right:0,background:D.panel,borderRadius:"20px 20px 0 0",borderTop:"1px solid "+D.border,padding:"20px 16px",transform:panelOpen?"translateY(0)":"translateY(100%)",transition:"transform .35s cubic-bezier(.4,0,.2,1)",zIndex:20,maxHeight:"75vh",overflowY:"auto"}}>
        <div onClick={closePanel} style={{width:"36px",height:"4px",background:D.border,borderRadius:"2px",margin:"-8px auto 16px",cursor:"pointer"}}/>
        {selectedDomain && (
          <>
            <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"16px"}}>
              <span style={{fontSize:"20px"}}>{selectedDomain.icon}</span>
              <div style={{fontFamily:D.serif,fontSize:"18px",color:D.h}}>{selectedDomain.name[lang]}</div>
            </div>
            {!selectedTrack && (
              <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                {selectedDomain.tracks.map(track => {
                  const prog = getProgress(selectedDomain.id, track.id);
                  const total = track.articles.length;
                  const pct = Math.round((prog / total) * 100);
                  return (
                    <div key={track.id} onClick={() => setSelectedTrack(track)}
                      style={{background:D.panel2,border:"1px solid "+D.border,borderRadius:"14px",padding:"14px 16px",cursor:"pointer",borderLeft:"3px solid "+selectedDomain.color,transition:"all .15s"}}
                      onMouseEnter={e=>{e.currentTarget.style.background=D.panel3;}}
                      onMouseLeave={e=>{e.currentTarget.style.background=D.panel2;}}
                    >
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"6px"}}>
                        <div style={{fontFamily:D.serif,fontSize:"15px",color:D.h}}>{track.name[lang]}</div>
                        <span style={{fontSize:"12px",color:D.ter}}>→</span>
                      </div>
                      <div style={{fontSize:"11px",color:D.ter,fontFamily:D.ui,marginBottom:"8px"}}>{track.desc[lang]}</div>
                      <div style={{height:"2px",background:D.border,borderRadius:"1px",overflow:"hidden"}}>
                        <div style={{height:"100%",background:selectedDomain.color,width:pct+"%",borderRadius:"1px"}}/>
                      </div>
                      <div style={{fontSize:"10px",color:D.ter,fontFamily:D.ui,marginTop:"4px"}}>
                        {prog > 0 ? prog+"/"+total+" "+t.trackProgress : total+" "+t.articles}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {selectedTrack && (
              <>
                <button onClick={() => setSelectedTrack(null)} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:"12px",color:D.ter,fontFamily:D.ui,padding:0,marginBottom:"12px"}}>
                  {"← "}{selectedDomain.name[lang]}
                </button>
                <div style={{fontFamily:D.serif,fontSize:"16px",color:D.h,marginBottom:"4px"}}>{selectedTrack.name[lang]}</div>
                <div style={{fontSize:"11px",color:D.ter,fontFamily:D.ui,marginBottom:"14px"}}>{selectedTrack.desc[lang]}</div>
                <div style={{display:"flex",alignItems:"center",gap:"10px",margin:"16px 0"}}>
                  <div style={{flex:1,height:"1px",background:D.borderSoft}}/>
                  <div style={{width:"5px",height:"5px",background:D.ember,transform:"rotate(45deg)",flexShrink:0}}/>
                  <div style={{flex:1,height:"1px",background:D.borderSoft}}/>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:"8px",marginBottom:"16px"}}>
                  {selectedTrack.articles.map((article, i) => {
                    const prog = getProgress(selectedDomain.id, selectedTrack.id);
                    const isDone = i < prog;
                    const isCurrent = i === prog;
                    return (
                      <div key={i} onClick={() => { onStartTrack(selectedDomain, selectedTrack, i); closePanel(); }}
                        style={{display:"flex",alignItems:"center",gap:"12px",padding:"12px 14px",borderRadius:"12px",background:isCurrent?selectedDomain.color+"18":D.panel2,border:"1px solid "+(isCurrent?selectedDomain.color:D.border),cursor:"pointer",opacity:isDone&&!isCurrent?0.55:1}}
                      >
                        <div style={{width:"24px",height:"24px",borderRadius:"50%",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"11px",background:isDone?selectedDomain.color:isCurrent?selectedDomain.color+"25":D.panel3,border:"1px solid "+(isDone||isCurrent?selectedDomain.color:D.border),color:isDone?"#fff":isCurrent?selectedDomain.color:D.ter}}>
                          {isDone ? "✓" : i+1}
                        </div>
                        <div style={{flex:1,fontFamily:D.serif,fontSize:"14px",color:D.h}}>{article[lang]}</div>
                        {isCurrent && <span style={{fontSize:"12px",color:selectedDomain.color}}>→</span>}
                      </div>
                    );
                  })}
                </div>
                <button onClick={() => { onStartTrack(selectedDomain, selectedTrack, getProgress(selectedDomain.id, selectedTrack.id)); closePanel(); }}
                  style={{width:"100%",background:selectedDomain.color,color:"#fff",border:"none",borderRadius:"14px",padding:"13px",fontFamily:D.serif,fontSize:"15px",cursor:"pointer",letterSpacing:".04em"}}>
                  {getProgress(selectedDomain.id, selectedTrack.id) > 0 ? t.continueTrack : t.startTrack}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Feedback Modal ─────────────────────────────────────────────────────────────

function FeedbackModal({ result, lang, lengthHint, toneHint, styleHint, onClose }) {
  const [tab,setTab]=useState("like");const [text,setText]=useState("");
  const [issue,setIssue]=useState("");const [rating,setRating]=useState(0);
  const [sent,setSent]=useState(false);const [sending,setSending]=useState(false);
  const send = async () => {
    if (tab==="message"&&!text.trim()) return;
    if (tab==="rate"&&rating===0) return;
    setSending(true);
    try {
      await fetch("/api/feedback",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({type:tab,article:result?.title||"",lang,length:lengthHint,tone:toneHint,style:styleHint,rating,text,issue})});
      setSent(true); setTimeout(()=>onClose(),2000);
    } catch(e) {} setSending(false);
  };
  const FM = {
    ru: { title:"Отзыв", thanks:"✓ Спасибо!", like:"👍 Нравится", issue:"🐛 Проблема", rate:"⭐ Оценить", message:"💬 Написать",
      likeText:"Отправь лайк разработчику — это помогает!",
      selectIssue:"Выбери тип проблемы...", cutOff:"История обрывается", wrongLang:"Неверный язык", wrongArticle:"Неверная статья",
      poorQuality:"Низкое качество", appBroken:"Приложение не работает", other:"Другое",
      writeThoughts:"Напиши свои мысли...", additionalDetails:"Дополнительные детали (опционально)...",
      sending:"Отправка...", send:"Отправить" },
    en: { title:"Feedback", thanks:"✓ Thanks!", like:"👍 Like", issue:"🐛 Issue", rate:"⭐ Rate", message:"💬 Message",
      likeText:"Send a like to the developer — it helps!",
      selectIssue:"Select issue type...", cutOff:"Story cut off", wrongLang:"Wrong language", wrongArticle:"Wrong article",
      poorQuality:"Poor quality", appBroken:"App broken", other:"Other",
      writeThoughts:"Write your thoughts...", additionalDetails:"Additional details (optional)...",
      sending:"Sending...", send:"Send" },
    de: { title:"Feedback", thanks:"✓ Danke!", like:"👍 Gefällt mir", issue:"🐛 Problem", rate:"⭐ Bewerten", message:"💬 Nachricht",
      likeText:"Sende ein Like an den Entwickler — es hilft!",
      selectIssue:"Problemtyp auswählen...", cutOff:"Geschichte bricht ab", wrongLang:"Falsche Sprache", wrongArticle:"Falscher Artikel",
      poorQuality:"Schlechte Qualität", appBroken:"App funktioniert nicht", other:"Andere",
      writeThoughts:"Schreib deine Gedanken...", additionalDetails:"Zusätzliche Details (optional)...",
      sending:"Wird gesendet...", send:"Senden" },
  };
  const fm = FM[lang] || FM.en;
  const TABS=[{id:"like",label:fm.like},{id:"issue",label:fm.issue},{id:"rate",label:fm.rate},{id:"message",label:fm.message}];
  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:"16px"}}>
      <div style={{background:D.panel,border:`1px solid ${D.border}`,borderRadius:"20px",padding:"24px",maxWidth:"400px",width:"100%"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"18px"}}>
          <span style={{fontSize:"16px",color:D.h,fontFamily:D.serif}}>{fm.title}</span>
          <button onClick={onClose} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:"20px",color:D.ter,lineHeight:1,padding:"0 4px"}}>×</button>
        </div>
        {sent ? <div style={{textAlign:"center",padding:"24px 0",color:D.emberBright,fontSize:"16px",fontFamily:D.serif}}>{fm.thanks}</div> : (<>
          <div style={{display:"flex",gap:"6px",marginBottom:"20px",flexWrap:"wrap"}}>
            {TABS.map(tb=><button key={tb.id} onClick={()=>setTab(tb.id)} style={{padding:"7px 14px",fontSize:"12px",borderRadius:"20px",cursor:"pointer",border:`1px solid ${tab===tb.id?D.ember:D.border}`,background:tab===tb.id?"rgba(196,119,74,.16)":"transparent",color:tab===tb.id?D.emberBright:D.sec,fontFamily:D.ui}}>{tb.label}</button>)}
          </div>
          {tab==="like"&&<p style={{fontSize:"13px",color:D.sec,fontFamily:D.ui,margin:"0 0 20px",lineHeight:1.6}}>{fm.likeText}</p>}
          {tab==="issue"&&<select value={issue} onChange={e=>setIssue(e.target.value)} style={{width:"100%",padding:"9px 12px",borderRadius:"10px",border:`1px solid ${D.border}`,background:D.panel3,color:D.h,fontSize:"13px",fontFamily:D.ui,marginBottom:"12px",outline:"none"}}><option value="">{fm.selectIssue}</option><option value="Story cut off">{fm.cutOff}</option><option value="Wrong language">{fm.wrongLang}</option><option value="Wrong article">{fm.wrongArticle}</option><option value="Poor quality">{fm.poorQuality}</option><option value="App broken">{fm.appBroken}</option><option value="Other">{fm.other}</option></select>}
          {tab==="rate"&&<div style={{display:"flex",gap:"8px",marginBottom:"20px",justifyContent:"center"}}>{[1,2,3,4,5].map(s=><button key={s} onClick={()=>setRating(s)} style={{fontSize:"30px",background:"transparent",border:"none",cursor:"pointer",opacity:s<=rating?1:0.25,transition:"opacity .15s"}}>⭐</button>)}</div>}
          {(tab==="issue"||tab==="rate"||tab==="message")&&<textarea value={text} onChange={e=>setText(e.target.value)} placeholder={tab==="message"?fm.writeThoughts:fm.additionalDetails} rows={3} style={{width:"100%",padding:"10px 12px",borderRadius:"12px",border:`1px solid ${D.border}`,background:D.panel3,color:D.h,fontSize:"13px",fontFamily:D.serif,outline:"none",resize:"none",boxSizing:"border-box",marginBottom:"16px"}}/>}
          <button onClick={send} disabled={sending} style={{width:"100%",padding:"11px",borderRadius:"12px",background:D.ember,color:"#fff",border:"none",fontSize:"13px",fontFamily:D.serif,fontWeight:"700",cursor:"pointer",opacity:sending?0.6:1}}>{sending?fm.sending:fm.send}</button>
        </>)}
      </div>
    </div>
  );
}


// ── Email Toggle ───────────────────────────────────────────────────────────────

function EmailToggle({ userId, lang }) {
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    supabase.from('user_preferences')
      .select('daily_email_enabled')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data }) => setEnabled(data?.daily_email_enabled ?? false));
  }, [userId]);

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    await supabase.from('user_preferences').upsert({
      user_id: userId,
      daily_email_enabled: next,
    }, { onConflict: 'user_id' });
  };

  if (enabled === null) return null;

  return (
    <button onClick={toggle} style={{
      background: enabled ? "rgba(196,119,74,.14)" : "transparent",
      border: `1px solid ${enabled ? D.ember : D.border}`,
      borderRadius: "20px", padding: "3px 12px", cursor: "pointer",
      fontSize: "11px", color: enabled ? D.emberBright : D.sec,
      fontFamily: D.ui, transition: "all .15s",
    }}>
      {enabled
        ? (lang === 'ru' ? '📧 Письмо дня ✓' : '📧 Daily digest ✓')
        : (lang === 'ru' ? '📧 Письмо дня' : '📧 Daily digest')}
    </button>
  );
}

// ── Main App ───────────────────────────────────────────────────────────────────

export default function WikiTale() {
  const [lang, setLang] = useState(() => {
    // ?lang=en/ru overrides for THIS load only — e.g. a visitor landing from
    // the English SEO page (see api/en.js) should see English immediately,
    // even if a previous visit saved a different language. Doesn't touch
    // localStorage — if they never change it via the UI dropdown, their
    // saved preference (or browser locale) still wins on their next visit.
    const queryLang = new URLSearchParams(window.location.search).get('lang');
    if (queryLang && ['ru', 'en'].includes(queryLang)) return queryLang;
    const saved = localStorage.getItem('wt_lang');
    if (saved && ['ru','en'].includes(saved)) return saved;
    const l = navigator.language?.slice(0,2);
    return ['ru','en'].includes(l) ? l : 'en';
  });
  const [input, setInput] = useState("");
  const [length, setLength] = useState(3);
  const [tone, setTone] = useState(3);
  const [style, setStyle] = useState(3);
  const [activePreset, setActivePreset] = useState(null);
  const [styleOpen, setStyleOpen] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [result, setResult] = useState(null);
  const [facts, setFacts] = useState([]);
  const [storyReady, setStoryReady] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [disambigResults, setDisambigResults] = useState([]);
  const [disambigQuery, setDisambigQuery] = useState("");
  const [reaction, setReaction] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [flashcards, setFlashcards] = useState([]);
  const [fcIndex, setFcIndex] = useState(0);
  const [fcRevealed, setFcRevealed] = useState(false);
  const [fcResults, setFcResults] = useState([]);
  const [fcLoading, setFcLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [authEmail, setAuthEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [trackProgress, setTrackProgress] = useState({});
  const [activeTrack, setActiveTrack] = useState(null);
  const [activeScreen, setActiveScreen] = useState("home");
  const [returnScreen, setReturnScreen] = useState("home"); // where to return on exit from reading cycle
  const [styleGate, setStyleGate] = useState(null); // { domain, track, articleIndex } when awaiting style choice
  const [styleSwitcherOpen, setStyleSwitcherOpen] = useState(false); // mid-read "change style" sheet
  const [interests, setInterests] = useState(() => {
    try { return JSON.parse(localStorage.getItem('wt_interests') || '[]'); } catch { return []; }
  });
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem('wt_onboarding_done'));
  const [showAuth, setShowAuth] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [hookStory, setHookStory] = useState(null);
  const [hookCard, setHookCard] = useState(null);
  const [hookLoading, setHookLoading] = useState(false);
  const [hookAnswered, setHookAnswered] = useState(null);

  // ИЗМЕНЕНИЕ 1: новые state переменные для XP/level/streak/weeklyGoal
  const [weeklyGoal, setWeeklyGoal] = useState(5);
  const [xp, setXp] = useState(0);
  const [level, setLevel] = useState(1);
  const [streak, setStreak] = useState(0);
  const [lastReadDate, setLastReadDate] = useState(null);

  const factsAbortRef = useRef(false);
  const showEmailCaptureRef = useRef(false);
  useEffect(() => { showEmailCaptureRef.current = showEmailCapture; }, [showEmailCapture]);

  // Android/Chrome install prompt: browser fires this once, we stash it and
  // trigger it later ourselves (on Victory), instead of letting Chrome show
  // its own mini-infobar at a random moment.
  const deferredInstallPromptRef = useRef(null);
  // Guards handleShare / handleShareFlashcard against a fast double-tap
  // firing the async share flow twice before the first call resolves —
  // was producing two attached images in one share (canvas generation +
  // navigator.share() both take real time, with no button-disable in between).
  const sharingRef = useRef(false);
  const postVictoryModalOpenRef = useRef(false);
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      deferredInstallPromptRef.current = e;
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleSetLang = async (newLang) => {
    setLang(newLang);
    localStorage.setItem('wt_lang', newLang);
    if (user) {
      await supabase.from('user_preferences').upsert({
        user_id: user.id, lang: newLang,
      }, { onConflict: 'user_id' });
    }
  };

  const t = I18N[lang];
  const isLoading = phase === "loading" || phase === "searching";

  const greetingLine = (() => {
    const hour = new Date().getHours();
    const greetings = {
      ru: hour < 12 ? "Доброе утро" : hour < 18 ? "Добрый день" : "Добрый вечер",
      en: hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening",
      de: hour < 12 ? "Guten Morgen" : hour < 18 ? "Guten Tag" : "Guten Abend",
    };
    const localeMap = { ru: "ru-RU", en: "en-US", de: "de-DE" };
    const dateStr = new Date().toLocaleDateString(localeMap[lang] || "en-US", { month: "long", day: "numeric" });
    return `${greetings[lang] || greetings.en} · ${dateStr}`;
  })();

  // ИЗМЕНЕНИЕ 2: обновлённый useEffect — загружаем все поля включая xp/level/streak
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u?.email === process.env.REACT_APP_INTERNAL_USER_EMAIL) {
        window.posthog?.identify(u.id, { is_internal: true });
      }
      if (u) {
        supabase.from('user_preferences')
          .select('lang, weekly_goal, xp, level, streak, last_read_date')
          .eq('user_id', u.id)
          .maybeSingle()
          .then(({ data }) => {
            if (!data) return;
            if (data.lang && ['ru','en'].includes(data.lang)) {
              setLang(data.lang);
              localStorage.setItem('wt_lang', data.lang);
            }
            if (data.weekly_goal) setWeeklyGoal(data.weekly_goal);
            if (data.xp) setXp(data.xp);
            if (data.level) setLevel(data.level);
            if (data.streak) setStreak(data.streak);
            if (data.last_read_date) setLastReadDate(data.last_read_date);
          }).catch(() => {});
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u && showEmailCaptureRef.current) {
        supabase.from('user_preferences').upsert({
          user_id: u.id, daily_email_enabled: true,
        }, { onConflict: 'user_id' }).catch(() => {});
        setShowEmailCapture(false);
        try { window.posthog?.capture('email_captured', { via: 'anonymous_signup' }); } catch (e) {}
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Deep link: /story/:id opens a shared story directly in the reading view,
  // skipping Home. Runs once on mount; clears the URL afterward so in-app
  // navigation (tab bar, exit button) doesn't fight with the browser history.
  //
  // The actual URL a real browser lands on is NOT /story/:id — Vercel rewrites
  // that path to api/story.js first (see vercel.json), which serves crawlers
  // (Telegram/Facebook/etc.) an OG-tag HTML page for link previews, and 302s
  // real browsers to /?story=:id instead (query param, not path — see that
  // file's `appDestination`). So the query string is the primary source here;
  // the path match is kept only as a defensive fallback for any link that
  // might point straight at /story/:id without going through that redirect.
  useEffect(() => {
    const queryId = new URLSearchParams(window.location.search).get('story');
    const pathMatch = window.location.pathname.match(/^\/story\/([a-zA-Z0-9-]+)\/?$/);
    const storyId = queryId || pathMatch?.[1];
    if (!storyId) return;
    supabase
      .from('shared_stories')
      .select('title, story, wiki_url, lang')
      .eq('id', storyId)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        if (data.lang && ['ru', 'en'].includes(data.lang)) {
          setLang(data.lang);
          localStorage.setItem('wt_lang', data.lang);
        }
        setActiveScreen("create");
        setActiveTrack(null);
        setResult({ title: data.title, story: data.story, url: data.wiki_url || '' });
        setPageIndex(0);
        setPhase("done");
        try { window.posthog?.capture('shared_story_opened', { story_id: storyId, title: data.title }); } catch (e) {}
      })
      .catch(() => {})
      .finally(() => {
        window.history.replaceState({}, '', '/');
      });
  }, []);

  // Deep link: /daily/:id opens a daily-digest email story directly,
  // same pattern as /story/:id above.
  useEffect(() => {
    const m = window.location.pathname.match(/^\/daily\/([a-zA-Z0-9-]+)\/?$/);
    if (!m) return;
    const storyId = m[1];
    supabase
      .from('daily_stories')
      .select('title, story, wiki_url, lang')
      .eq('id', storyId)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        if (data.lang && ['ru', 'en'].includes(data.lang)) {
          setLang(data.lang);
          localStorage.setItem('wt_lang', data.lang);
        }
        setActiveScreen("create");
        setActiveTrack(null);
        setResult({ title: data.title, story: data.story, url: data.wiki_url || '' });
        setPageIndex(0);
        setPhase("done");
        try { window.posthog?.capture('daily_email_story_opened', { story_id: storyId, title: data.title }); } catch (e) {}
      })
      .catch(() => {})
      .finally(() => {
        window.history.replaceState({}, '', '/');
      });
  }, []);

  // Cleans up ?lang= from the URL after the initial useState read above has
  // already used it — same tidy-up-the-address-bar pattern as the deep-link
  // effects below, just via a targeted param delete instead of a full reset
  // to "/", since ?lang= could in principle coexist with other params.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('lang')) {
      params.delete('lang');
      const rest = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
  }, []);

  // Deep link: /hook/:id — SMM entry point. Fetches a library story (must be
  // public, hence is_library=true) and shows a True/False teaser card, no
  // login required. This is the intended landing target for TikTok/Instagram
  // links, replacing direct /story/:id or /daily/:id links for that traffic.
  useEffect(() => {
    const m = window.location.pathname.match(/^\/hook\/([a-zA-Z0-9-]+)\/?$/);
    if (!m) return;
    const storyId = m[1];
    supabase
      .from('track_stories')
      .select('id, title, story, wiki_url, lang, pull_quote, images, flashcards(type, question, answer, is_hook)')
      .eq('id', storyId)
      .eq('is_library', true)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        if (data.lang && ['ru', 'en'].includes(data.lang)) {
          setLang(data.lang);
          localStorage.setItem('wt_lang', data.lang);
        }
        const prefetchedCard = (data.flashcards || []).find(c => c.is_hook) || (data.flashcards || [])[0] || null;
        setHookStory({ id: data.id, title: data.title, story: data.story, wiki_url: data.wiki_url, lang: data.lang, pullQuote: data.pull_quote || null, sceneImages: data.images || [], prefetchedCard });
        try { window.posthog?.capture('hook_card_shown', { story_id: data.id, title: data.title, prefetched: !!prefetchedCard }); } catch (e) {}
      })
      .catch(() => {})
      .finally(() => {
        window.history.replaceState({}, '', '/');
      });
  }, []);

  // True/False teaser for the current hookStory. Fast path: B3 pre-generates
  // flashcards for every library story, so hookStory.prefetchedCard is present
  // almost always — instant, no spinner. Fallback: live generation only for
  // stories not yet covered by the backfill (see generate-library.js mode
  // "backfill_flashcards").
  useEffect(() => {
    if (!hookStory) { setHookCard(null); return; }
    setHookAnswered(null);
    if (hookStory.prefetchedCard) {
      setHookCard(hookStory.prefetchedCard);
      setHookLoading(false);
      return;
    }
    let cancelled = false;
    setHookLoading(true);
    generateFlashcards(hookStory.title, hookStory.story, hookStory.lang || lang)
      .then(cards => {
        if (cancelled) return;
        const tf = cards.filter(c => c.type === 'truefalse');
        setHookCard(tf.length ? tf[Math.floor(Math.random() * tf.length)] : (cards[0] || null));
      })
      .catch(() => { if (!cancelled) setHookCard(null); })
      .finally(() => { if (!cancelled) setHookLoading(false); });
    return () => { cancelled = true; };
  }, [hookStory]);

  useEffect(() => {
    if (!user) return;
    supabase.from('user_track_progress').select('domain_id, track_id, article_index')
      .eq('user_id', user.id).then(({ data }) => {
        if (!data) return;
        const p = {};
        data.forEach(r => { p[`${r.domain_id}_${r.track_id}`] = r.article_index; });
        setTrackProgress(p);
      });
  }, [user]);

  const signInWithGoogle = async () => {
    await supabase.auth.signInWithOAuth({ provider:'google', options:{ redirectTo:'https://wiki-tale.app' } });
  };
  const signInWithMagicLink = async () => {
    if (!authEmail.trim()) return;
    const { error } = await supabase.auth.signInWithOtp({ email:authEmail, options:{ emailRedirectTo:'https://wiki-tale.app' } });
    if (!error) setMagicSent(true);
  };
  const signOut = async () => { await supabase.auth.signOut(); setUser(null); };

  const updateTrackProgress = async (domain, track, articleIndex) => {
      const key = `${domain.id}_${track.id}`;
      const newIndex = articleIndex + 1;
      setTrackProgress(prev => ({ ...prev, [key]: newIndex }));
      const { data: { session } } = await supabase.auth.getSession();
      const currentUser = session?.user;
      console.log('[progress] currentUser:', currentUser?.id, currentUser?.email);
      if (currentUser) {
        const { data, error } = await supabase.from('user_track_progress').upsert({
          user_id: currentUser.id, domain_id: domain.id, track_id: track.id, article_index: newIndex,
        }, { onConflict: 'user_id,domain_id,track_id' });
        console.log('[progress] upsert result:', { data, error });
      } else {
        console.log('[progress] NO USER — skipping DB write');
      }
    };

  const generateTrack = async (domain, track, preset, lang) => {
    if (!user) return;
    const articles = track.articles.map(a => a[lang] || a['en']);
    try {
      await fetch('/api/generate-track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          domain_id: domain.id,
          track_id: track.id,
          articles,
          preset,
          lang,
        }),
      });
    } catch (e) {}
  };

  // ИЗМЕНЕНИЕ 3: функции saveUserPrefs, addXp, saveWeeklyGoal
  const saveUserPrefs = async (updates) => {
    // Same fix as updateTrackProgress: don't trust stale `user` React state.
    const { data: { session } } = await supabase.auth.getSession();
    const currentUser = session?.user;
    if (!currentUser) return;
    await supabase.from('user_preferences').upsert({
      user_id: currentUser.id,
      ...updates,
     }, { onConflict: 'user_id' });
  };

  const addXp = async (points = 10) => {
    const newXp = xp + points;
    const LEVELS = [
      { level: 1, name: 'Любопытный', min: 0, max: 50 },
      { level: 2, name: 'Эрудит', min: 50, max: 150 },
      { level: 3, name: 'Мыслитель', min: 150, max: 350 },
      { level: 4, name: 'Мудрец', min: 350, max: 999999 },
    ];
    const newLevel = LEVELS.findLast(l => newXp >= l.min)?.level || 1;
    setXp(newXp);
    setLevel(newLevel);

    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    let newStreak = streak;
    if (lastReadDate === today) {
      // уже читал сегодня — streak не меняем
    } else if (lastReadDate === yesterday) {
      newStreak = streak + 1;
    } else {
      newStreak = 1;
    }
    setStreak(newStreak);
    setLastReadDate(today);

    await saveUserPrefs({
      xp: newXp,
      level: newLevel,
      streak: newStreak,
      last_read_date: today,
    });
  };

  const saveWeeklyGoal = async (goal) => {
    setWeeklyGoal(goal);
    await saveUserPrefs({ weekly_goal: goal });
  };

  // ИЗМЕНЕНИЕ 4: вызов addXp перед setPhase("done") в функции run
  const run = async (overrideInput=null, skipDisambig=false, trackContext=null, overrideSettings=null) => {
    const query = overrideInput || input;
    if (!query.trim()) return;
    const _length = overrideSettings?.length ?? length;
    const _tone   = overrideSettings?.tone   ?? tone;
    const _style  = overrideSettings?.style  ?? style;
    factsAbortRef.current = true;
    setErrorMsg(""); setResult(null); setFacts([]); setStoryReady(false);
    const { isUrl, topic } = parseWikiInput(query);
    if (!isUrl && !skipDisambig) {
      setPhase("searching");
      try {
        const results = await searchArticles(topic, lang);
        if (results && results.length >= 2) {
          setDisambigResults(results); setDisambigQuery(topic);
          setPhase("disambig"); return;
        }
      } catch(e) {}
    }
    setStyleOpen(false);
    setPhase("loading");
    try {
      factsAbortRef.current = false;
      setTimeout(() => {
        streamFacts(topic, lang, (fact) => { setFacts(prev => [...prev, fact]); }, factsAbortRef);
      }, 0);
      const storyResult = await generateStory(query, { length: _length, tone: _tone, style: _style, uiLang:lang, presetId: activePreset });
      setResult(storyResult);
      try { window.posthog?.capture('story_generated', { title: storyResult.title, lang, length: _length, tone: _tone, style: _style, from_track: !!trackContext }); } catch(e) {}
      if (trackContext) {
        await updateTrackProgress(trackContext.domain, trackContext.track, trackContext.articleIndex);
      }
      await new Promise(r => setTimeout(r, 400));
      await addXp(10); 
      setPageIndex(0);
      setPhase("done");
    } catch(e) {
      if (e.message === 'free_limit_reached') setPhase("paywall");
      else { setErrorMsg(e.message); setPhase("error"); }
    }
  };

  const handleOpenDailyStory = (dailyStory) => {
    setReturnScreen(activeScreen);
    setActiveScreen("create");
    setInput(dailyStory.title);
    setResult({ title: dailyStory.title, story: dailyStory.story, url: dailyStory.wiki_url || '' });
    setPageIndex(0);
    setPhase("done");
    setFacts([]);
    setStoryReady(false);
    setActiveTrack(null);
    setActivePreset(dailyStory.preset || null);
    setReaction(null);
  };

  const handleStartCard = (card, preset) => {
    setReturnScreen(activeScreen);
    setActivePreset(preset.id);
    setLength(preset.length);
    setTone(preset.tone);
    setStyle(preset.style);
    setInput(card.article[lang]);
    setActiveTrack({ domain: card.domain, track: card.track, articleIndex: card.articleIndex });
    setActiveScreen("create");
    setPhase("idle");
    setResult(null);
    setFacts([]);
    setStoryReady(false);
    setStyleOpen(false);
    run(card.article[lang], true,
      { domain: card.domain, track: card.track, articleIndex: card.articleIndex },
      { length: preset.length, tone: preset.tone, style: preset.style }
    );
  };

  const handleStartTrack = async (domain, track, articleIndex, preset) => {
  console.log('[nav] handleStartTrack called', { domainId: domain?.id, trackId: track?.id, articleIndex, preset, activeScreenNow: activeScreen });
  setReturnScreen(activeScreen);
  const article = track.articles[articleIndex];
  if (!article) return;

  // First chapter of a track and no style was explicitly chosen for this run — ask first
  if (articleIndex === 0 && !preset) {
    setStyleGate({ domain, track, articleIndex });
    return;
  }

  const resolvedPreset = preset || activePreset || 'popscience';

  setActiveTrack({ domain, track, articleIndex });
  setActiveScreen("create");
  setInput(article[lang]);
  setResult(null);
  setFacts([]);
  setStoryReady(false);
  setActivePreset(resolvedPreset);

  // Academic — всегда генерируем живьём
  if (resolvedPreset === 'academic') {
    setPhase("idle");
    setStyleOpen(true);
    generateTrack(domain, track, resolvedPreset, lang);
    return;
  }

  // Popscience / eli8 — ищем в библиотеке
  setPhase("loading");
  const { data, error } = await supabase
    .from('track_stories')
    .select('id, title, story, wiki_url, pull_quote, images')
    .eq('is_library', true)
    .eq('domain_id', domain.id)
    .eq('track_id', track.id)
    .eq('article_index', articleIndex)
    .eq('preset', resolvedPreset)
    .eq('lang', lang)
    .order('engine_version', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (data?.story) {
    // Нашли в библиотеке — показываем мгновенно
    setResult({ id: data.id, title: data.title, story: data.story, url: data.wiki_url || '', pullQuote: data.pull_quote || null, sceneImages: data.images || [] });
    await updateTrackProgress(domain, track, articleIndex);
    await addXp(10);
    setPageIndex(0);
    setPhase("done");
  } else {
    // Не нашли — генерируем живьём и сохраняем в БД
    setPhase("idle");
    generateTrack(domain, track, resolvedPreset, lang);
    run(article[lang], true, { domain, track, articleIndex });
  }
};

  // Style gate: user picked a style for the pending track — start it now
  const handleStyleGateConfirm = (chosenPreset) => {
    if (!styleGate) return;
    const { domain, track, articleIndex } = styleGate;
    setStyleGate(null);
    handleStartTrack(domain, track, articleIndex, chosenPreset);
  };

  // Mid-read style switcher: apply the new style starting from the NEXT chapter,
  // finish the current page in the style already loaded.
  const handleStyleSwitch = (chosenPreset) => {
    setStyleSwitcherOpen(false);
    setActivePreset(chosenPreset);
  };

  const handleNextInTrack = () => {
    console.log('[nav] handleNextInTrack called', { activeTrack });
    if (!activeTrack) { console.log('[nav] NO activeTrack — bailing'); return; }
    const { domain, track, articleIndex } = activeTrack;
    const nextIndex = articleIndex + 1;
    console.log('[nav] nextIndex:', nextIndex, 'track length:', track.articles.length);
    if (nextIndex < track.articles.length) {
      handleStartTrack(domain, track, nextIndex, activePreset);
    } else {
      console.log('[nav] END OF TRACK — going home');
      setActiveTrack(null);
      setActiveScreen("home");
      setPhase("idle");
    }
  };

// Reading cycle handlers: reading → flashcards → victory → next
  const goToFlashcards = async () => {
    if (!result) return;
    try { window.posthog?.capture('chapter_completed', { story_id: result.id || null, title: result.title }); } catch (e) {}
    setFcLoading(true);
    setFcIndex(0);
    setFcRevealed(false);
    setFcResults([]);
    setPhase("flashcards");
    try {
      let cards = [];
      // Fast path: library story with pre-generated cards (see B3) — no spinner needed,
      // but we still show it briefly since this runs as a real fetch either way.
      if (result.id) {
        const { data } = await supabase
          .from('flashcards')
          .select('type, question, answer')
          .eq('story_id', result.id);
        if (data && data.length) cards = data;
      }
      // Fallback: on-demand story (academic preset, custom topic, or a library
      // story whose flashcards haven't been backfilled yet) — generate live.
      if (!cards.length) {
        cards = await generateFlashcards(result.title, result.story, lang);
      }
      setFlashcards(cards.length ? cards : []);
    } catch (e) {
      // Don't block the cycle if generation fails — empty set skips straight to victory
      console.error('[flashcards] generation failed:', e);
      setFlashcards([]);
    } finally {
      setFcLoading(false);
    }
  };

  // Exit reading cycle: return to whichever screen the user came from, reset everything
  const exitReading = () => {
    setActiveScreen(returnScreen);
    setPhase("idle");
    setResult(null);
    setActiveTrack(null);
    setFlashcards([]);
    setFcResults([]);
    setFcRevealed(false);
    setFcIndex(0);
    setPageIndex(0);
  };

    const answerFlashcard = (gotIt) => {
    const finalResults = [...fcResults, gotIt];
    if (fcIndex + 1 < flashcards.length) {
      setFcResults(finalResults);
      setFcIndex(fcIndex + 1);
      setFcRevealed(false);
    } else {
      // Last card — persist recall to localStorage, then go to victory
      try {
        const got = finalResults.filter(Boolean).length;
        const log = JSON.parse(localStorage.getItem('wt_recall') || '[]');
        log.push({ title: result?.title, got, total: finalResults.length, ts: Date.now() });
        localStorage.setItem('wt_recall', JSON.stringify(log.slice(-200)));
      } catch (e) {}
      setFcResults(finalResults);
      setPhase("victory");
    }
  };

  const backFlashcard = () => {
    if (fcIndex > 0) {
      setFcIndex(fcIndex - 1);
      setFcRevealed(false);
    }
  };

  // Email capture: shown once after a Victory screen, either to get an anonymous
  // user to sign in (and auto-subscribe), or to nudge an already-signed-in,
  // not-yet-subscribed user to turn the daily digest on in one tap.
  const maybeShowEmailCapture = () => {
    if (user) {
      const dismissed = localStorage.getItem('wt_email_capture_dismissed');
      if (dismissed) return;
      supabase.from('user_preferences').select('daily_email_enabled').eq('user_id', user.id).maybeSingle()
        .then(({ data }) => {
          if (data?.daily_email_enabled === false && !postVictoryModalOpenRef.current) {
            postVictoryModalOpenRef.current = true;
            setShowEmailCapture(true);
          }
        })
        .catch(() => {});
      return;
    }
    const shown = localStorage.getItem('wt_email_capture_shown');
    if (!shown) {
      localStorage.setItem('wt_email_capture_shown', '1');
      postVictoryModalOpenRef.current = true;
      setShowEmailCapture(true);
    }
  };

  const handleEmailCaptureSkip = () => {
    setShowEmailCapture(false);
    localStorage.setItem('wt_email_capture_dismissed', '1');
  };

  const handleAlreadyAuthSubscribe = async () => {
    if (!user) return;
    await supabase.from('user_preferences').upsert({
      user_id: user.id, daily_email_enabled: true,
    }, { onConflict: 'user_id' }).catch(() => {});
    setShowEmailCapture(false);
    try { window.posthog?.capture('email_captured', { via: 'already_logged_in' }); } catch (e) {}
  };

  // Install prompt: nudge to add WikiTale to the home screen. Starts from the
  // 2nd completed story (not the 1st — that slot belongs to email capture, see
  // priority ordering above), never shows inside an already-installed PWA, and
  // never stacks with the email capture modal on the same Victory screen.
  const incrementStoriesCompleted = () => {
    const n = parseInt(localStorage.getItem('wt_stories_completed_count') || '0', 10) + 1;
    localStorage.setItem('wt_stories_completed_count', String(n));
    return n;
  };

  const maybeShowInstallPrompt = (completedCount) => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) return;
    if (postVictoryModalOpenRef.current) return; // email capture already claimed this Victory
    if (completedCount < 2) return;

    const shownAt = localStorage.getItem('wt_install_prompt_shown_at');
    const dismissCount = parseInt(localStorage.getItem('wt_install_prompt_dismiss_count') || '0', 10);
    const lastShownCount = parseInt(localStorage.getItem('wt_install_prompt_shown_count') || '0', 10);

    const shouldShow = !shownAt || (dismissCount > 0 && completedCount - lastShownCount >= 3);
    if (!shouldShow) return;

    postVictoryModalOpenRef.current = true;
    setShowInstallPrompt(true);
    localStorage.setItem('wt_install_prompt_shown_at', String(Date.now()));
    localStorage.setItem('wt_install_prompt_shown_count', String(completedCount));
    try { window.posthog?.capture('install_prompt_shown', { completed_count: completedCount }); } catch (e) {}
  };

  const handleInstallPromptDismiss = () => {
    setShowInstallPrompt(false);
    const c = parseInt(localStorage.getItem('wt_install_prompt_dismiss_count') || '0', 10) + 1;
    localStorage.setItem('wt_install_prompt_dismiss_count', String(c));
    try { window.posthog?.capture('install_prompt_dismissed'); } catch (e) {}
  };

  const handleInstallPromptAccept = async () => {
    const dp = deferredInstallPromptRef.current;
    if (dp) {
      dp.prompt();
      try { await dp.userChoice; } catch (e) {}
      deferredInstallPromptRef.current = null;
    }
    setShowInstallPrompt(false);
    try { window.posthog?.capture('install_prompt_accepted'); } catch (e) {}
  };

  const handleHookAnswer = (guess) => {
    setHookAnswered(guess);
    try { window.posthog?.capture('hook_card_answered', { story_id: hookStory?.id, correct: guess === hookCard?.answer }); } catch (e) {}
  };

  const handleHookReadFull = () => {
    if (!hookStory) return;
    try { window.posthog?.capture('hook_expanded_to_story', { story_id: hookStory.id }); } catch (e) {}
    setResult({ id: hookStory.id, title: hookStory.title, story: hookStory.story, url: hookStory.wiki_url || '', pullQuote: hookStory.pullQuote || null, sceneImages: hookStory.sceneImages || [] });
    setPageIndex(0);
    setPhase("done");
    setActiveScreen("create");
    setActiveTrack(null);
    setReturnScreen("home");
    setHookStory(null);
    setHookCard(null);
    setHookAnswered(null);
  };

  const handleHookAnother = async () => {
    const next = await pickRandomHookStory(lang);
    if (next) setHookStory(next);
  };

  const handleHookClose = () => {
    setHookStory(null);
    setHookCard(null);
    setHookAnswered(null);
  };

  const finishVictory = () => {
    postVictoryModalOpenRef.current = false;
    const completedCount = incrementStoriesCompleted();
    maybeShowEmailCapture();
    maybeShowInstallPrompt(completedCount);
    // Next chapter in track if exists, otherwise back to progress screen
    if (activeTrack && activeTrack.articleIndex + 1 < activeTrack.track.articles.length) {
      handleNextInTrack();
    } else {
      setActiveScreen("game");
      setPhase("idle");
    }
    setResult(null);
    setFlashcards([]);
  };

  const handleJumpToTrack = (newIndex) => {
    if (!activeTrack) return;
    const { domain, track } = activeTrack;
    handleStartTrack(domain, track, newIndex, activePreset);
  };

  const handleRegen = () => {
    factsAbortRef.current = true;
    setPhase("idle"); setResult(null); setFacts([]); setStoryReady(false);
    setStyleOpen(false); setReaction(null); setActiveTrack(null);
  };

  const handleReaction = async (type) => {
    if (reaction) return;
    setReaction(type);
    try { await fetch("/api/feedback",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({type: type==="dislike"?"dislike":"like",article:result?.title||"",lang,length:t.lengthHints[length-1],tone:t.toneLabels[tone-1],style:t.styleLabels[style-1]})}); } catch(e) {}
  };

  // Draws a vertical (9:16) share card on an offscreen canvas and returns it as a
  // File, ready to hand to navigator.share({ files: [...] }). Used both for the
  // Victory-screen achievement card and the flashcard true/false teaser card.
  const generateShareCard = async ({ eyebrow, headline, footer, big }) => {
    const W = 1080, H = 1920;
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');

    // Background gradient, dark → warm
    const bg = ctx.createLinearGradient(0, 0, W * 0.3, H);
    bg.addColorStop(0, '#0D1017');
    bg.addColorStop(0.55, '#06080D');
    bg.addColorStop(1, '#1a1410');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Logo mark: small ember circle + wordmark, top-left
    ctx.beginPath();
    ctx.arc(84, 108, 20, 0, Math.PI * 2);
    const dot = ctx.createRadialGradient(78, 100, 2, 84, 108, 24);
    dot.addColorStop(0, '#E8924E');
    dot.addColorStop(1, '#C4774A');
    ctx.fillStyle = dot;
    ctx.fill();
    ctx.fillStyle = '#E8DCC8';
    ctx.font = '500 34px "Hanken Grotesk", sans-serif';
    ctx.textBaseline = 'middle';
    ctx.fillText('WikiTale', 120, 108);

    // Eyebrow label — now the dominant element, sized bigger than the
    // headline body text. Auto-shrinks (never wraps to a second line) if it
    // would overflow the card width — same safety approach as the headline
    // below, since a fixed size risks overflow with real browser font metrics.
    ctx.fillStyle = '#C4774A';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.save();
    const eyebrowText = (eyebrow || '').toUpperCase();
    const eyebrowMaxWidth = W * 0.85;
    let eyebrowFontSize = big ? 40 : 92;
    ctx.font = `600 ${eyebrowFontSize}px "Hanken Grotesk", sans-serif`;
    ctx.letterSpacing = '3px'; // tracked-out uppercase reads editorial, not shouty-bold
    while (ctx.measureText(eyebrowText).width > eyebrowMaxWidth && eyebrowFontSize > 32) {
      eyebrowFontSize -= 4;
      ctx.font = `600 ${eyebrowFontSize}px "Hanken Grotesk", sans-serif`;
    }
    const eyebrowY = big ? H * 0.36 : H * 0.4;
    ctx.fillText(eyebrowText, W / 2, eyebrowY);
    ctx.restore();

    // Headline — wraps across multiple lines, centered. Font size shrinks to fit
    // long questions instead of silently truncating them.
    ctx.fillStyle = '#E8DCC8';
    const maxWidth = W * 0.8;
    const maxLines = big ? 3 : 8;
    const availableHeight = big ? H * 0.42 : H * 0.4; // vertical budget before footer zone

    function wrapText(text, fontSize) {
      ctx.font = `400 ${fontSize}px "Newsreader", serif`;
      const words = text.split(' ');
      const lines = [];
      let line = '';
      for (const word of words) {
        const test = line ? `${line} ${word}` : word;
        if (ctx.measureText(test).width > maxWidth && line) {
          lines.push(line);
          line = word;
        } else {
          line = test;
        }
      }
      if (line) lines.push(line);
      return lines;
    }

    // Try decreasing font sizes until the wrapped text fits both the line-count
    // and vertical-space budgets.
    let fontSize = big ? 130 : 78;
    let lines = wrapText(headline, fontSize);
    let lineHeight = fontSize * 1.3;
    while ((lines.length > maxLines || lines.length * lineHeight > availableHeight) && fontSize > 32) {
      fontSize -= 4;
      lines = wrapText(headline, fontSize);
      lineHeight = fontSize * 1.3;
    }
    ctx.font = `400 ${fontSize}px "Newsreader", serif`;
    ctx.textAlign = 'center';
    let startY = big ? eyebrowY + 130 : eyebrowY + 150;
    lines.forEach((l, i) => {
      ctx.fillText(l, W / 2, startY + i * lineHeight);
    });

    // Footer
    if (footer) {
      ctx.fillStyle = '#5A6070';
      ctx.font = '400 30px "Hanken Grotesk", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(footer, W / 2, H - 140);
    }
    ctx.fillStyle = '#8A6A50';
    ctx.font = '500 28px "Hanken Grotesk", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('wiki-tale.app', W / 2, H - 90);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob ? new File([blob], 'wikitale-share.png', { type: 'image/png' }) : null);
      }, 'image/png');
    });
  };


  const handleShare = async () => {
    if (!result || sharingRef.current) return;
    sharingRef.current = true;
    let shareUrl = window.location.origin;
    try {
      const { data, error } = await supabase
        .from('shared_stories')
        .insert({ title: result.title, story: result.story, wiki_url: result.url, lang })
        .select('id')
        .single();
      if (!error && data?.id) {
        shareUrl = `https://wiki-tale.app/story/${data.id}`;
      }
    } catch (e) {}
    const sents = result.story.replace(/\n/g," ").split(/(?<=[.!?]) +/);
    const teaser = sents.slice(0,3).join(" ");
    // Web Share API's `url` field is separate from `text` on purpose. Some
    // platforms (confirmed: Instagram Stories) append `url` after `text`
    // verbatim when building their native share sheet — embedding the link
    // in BOTH fields produces a visibly duplicated URL. So: text WITHOUT
    // the link for navigator.share() (link goes in its own `url` field);
    // text WITH the link only for the clipboard fallback below, which has
    // no separate `url` field to carry it.
    const shareTextBody = `${result.title}\n\n${teaser}...`;
    try {
      const card = streak > 1
        ? await generateShareCard({
            eyebrow: lang === "ru" ? `${streak} дней подряд` : `${streak}-day streak`,
            headline: lang === "ru" ? "учу что-то новое каждый день" : "learning something new every day",
            footer: lang === "ru" ? `Последнее: «${result.title}»` : `Latest: "${result.title}"`,
            big: false,
          })
        : await generateShareCard({
            eyebrow: lang === "ru" ? "Только что понял" : "Just understood",
            headline: result.title,
            footer: lang === "ru" ? "Читай истории из Википедии" : "Read stories from Wikipedia",
            big: false,
          });
      if (navigator.share) {
        const shareData = { title: result.title, text: shareTextBody, url: shareUrl };
        if (card && navigator.canShare?.({ files: [card] })) shareData.files = [card];
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${shareTextBody}\n\n${shareUrl}`);
        setCopied(true); setTimeout(()=>setCopied(false),2000);
      }
    } catch(e) {
    } finally {
      sharingRef.current = false;
    }
  };

  // Share a true/false flashcard question as a teaser card — the question is shown,
  // the answer is withheld, driving the recipient back to the app to find out.
  const handleShareFlashcard = async (card) => {
    if (!card || !result || sharingRef.current) return;
    sharingRef.current = true;
    let shareUrl = window.location.origin;
    try {
      const { data, error } = await supabase
        .from('shared_stories')
        .insert({ title: result.title, story: result.story, wiki_url: result.url, lang })
        .select('id')
        .single();
      if (!error && data?.id) shareUrl = `https://wiki-tale.app/story/${data.id}`;
    } catch (e) {}
    // Same fix as handleShare above: no URL embedded in the text passed to
    // navigator.share() — it goes in shareData.url instead. The clipboard
    // fallback text keeps the URL inline since it has nowhere else to put it.
    const shareTextBody = lang === "ru"
      ? `Правда или ложь: ${card.question}\n\nУзнай ответ и историю целиком →`
      : `True or false: ${card.question}\n\nFind out the answer →`;
    try {
      // The eyebrow above already says "ПРАВДА ИЛИ ЛОЖЬ" / "TRUE OR FALSE" —
      // card.question itself often ends with the same phrase (that's how the
      // flashcard prompt asks the model to phrase it, so it reads standalone
      // in the practice screen). On the share card specifically that reads as
      // a redundant repeat, so strip a trailing "Правда или ложь?" / "True or
      // false?" (with any leading punctuation/space) just for this headline —
      // the underlying stored question text elsewhere is untouched.
      const cardHeadline = card.question
        .replace(/[.,!?\s]*(Правда или ложь|True or false)\??\s*$/i, '')
        .trim();
      const cardImg = await generateShareCard({
        eyebrow: lang === "ru" ? "Правда или ложь" : "True or false",
        headline: cardHeadline,
        footer: lang === "ru" ? "Узнай ответ и историю целиком" : "Find out the answer",
        big: false,
      });
      if (navigator.share) {
        const shareData = { title: result.title, text: shareTextBody, url: shareUrl };
        if (cardImg && navigator.canShare?.({ files: [cardImg] })) shareData.files = [cardImg];
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${shareTextBody}\n${shareUrl}`);
        setCopied(true); setTimeout(()=>setCopied(false),2000);
      }
    } catch (e) {
    } finally {
      sharingRef.current = false;
    }
  };


  const handleCopy = () => {
    if (!result) return;
    navigator.clipboard.writeText(`${result.title}\n\n${result.story}`).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2000); });
  };

  const handleLogoClick = () => {
    factsAbortRef.current = true;
    setPhase("idle"); setResult(null); setInput(""); setFacts([]);
    setStoryReady(false); setActiveTrack(null); setActiveScreen("home");
  };

  return (
    <div style={{minHeight:"100vh",background:D.bg,color:D.body,fontFamily:D.ui,paddingBottom:"80px",overflowX:"hidden",WebkitTextSizeAdjust:"100%"}}>
      <style>{`
        input::placeholder{color:#b0a898;opacity:1;font-style:italic}
        @keyframes wt_fadein{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes wt_btnrise{0%{opacity:0;transform:translateY(20px)}60%{opacity:.8}100%{opacity:1;transform:translateY(0)}}
        @keyframes wt_slideup{from{transform:translateY(100%)}to{transform:translateY(0)}}
        @keyframes wt_breathe{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.06);opacity:.85}}
        @keyframes wt_cardin{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes wt_shimmer{0%{background-position:-200px 0}100%{background-position:200px 0}}
        @keyframes wt_progress{0%{background-position:200% 0}100%{background-position:-200% 0}}
        .wt-breathing{animation:wt_breathe 4s ease-in-out infinite}
        .wt-card-anim{opacity:0;animation:wt_cardin .5s cubic-bezier(.34,1.56,.64,1) forwards}
        .wt-card-anim.pressed{transform:scale(.97) !important}
        .wt-shimmer{height:64px;border-radius:10px;margin-bottom:8px;background:linear-gradient(90deg,#EDE9E3 25%,#E2DDD4 37%,#EDE9E3 63%);background-size:400px 100%;animation:wt_shimmer 1.4s ease-in-out infinite}
        .wt-shimmer-dark{height:64px;border-radius:10px;margin-bottom:8px;background:linear-gradient(90deg,#12161f 25%,#1a1f2b 37%,#12161f 63%);background-size:400px 100%;animation:wt_shimmer 1.4s ease-in-out infinite}
        *{box-sizing:border-box}
      `}</style>
      {showOnboarding && (
        <OnboardingModal
          t={t} lang={lang}
          onLangChange={(l) => handleSetLang(l)}
          onDone={(selected) => { setInterests(selected); setShowOnboarding(false); setActiveScreen("game"); }}
        />
      )}


      <header style={{borderBottom:`1px solid ${D.borderSoft}`,marginBottom:"0",background:D.bg}}>
        <div style={{textAlign:"center",padding:"20px 20px 12px"}}>
          <div onClick={handleLogoClick} style={{cursor:"pointer",display:"inline-flex",flexDirection:"column",alignItems:"center"}}>
            <div className={!isLoading ? "wt-breathing" : ""}>
              <TreeLogo animate={isLoading}/>
            </div>
            <h1 style={{margin:0,fontSize:"26px",fontWeight:"400",letterSpacing:"0.18em",color:D.h,fontFamily:D.serif}}>WikiTale</h1>
            <p style={{margin:"4px 0 0",fontSize:"12px",color:D.sec,fontFamily:D.ui,letterSpacing:"0.01em"}}>
              {lang==="ru"?"Знания — насовсем":"Knowledge you'll keep"}
            </p>
          </div>
        </div>
        <div style={{display:"flex",gap:"6px",alignItems:"center",flexWrap:"wrap",justifyContent:"center",padding:"0 16px 10px"}}>
          <LanguageDropdown lang={lang} setLang={handleSetLang}/>
          {user ? (
            <>
              <EmailToggle userId={user.id} lang={lang} />
              <button onClick={signOut} style={{background:"transparent",border:`1px solid ${D.border}`,borderRadius:"20px",padding:"3px 12px",cursor:"pointer",fontSize:"11px",color:D.sec,fontFamily:D.ui}}>
                {user.email?.split('@')[0]} · {t.signOut}
              </button>
            </>
          ) : (
            <button onClick={()=>setShowAuth(true)} style={{background:"transparent",border:`1px solid ${D.ember}`,borderRadius:"20px",padding:"3px 12px",cursor:"pointer",fontSize:"11px",color:D.emberBright,fontFamily:D.ui}}>
              {lang==="ru"?"Войти":lang==="de"?"Anmelden":"Sign in"}
            </button>
          )}
        </div>
      </header>

      {activeScreen === "home" && (
        <HomeScreen
          t={t} lang={lang} interests={interests} trackProgress={trackProgress}
          xp={xp} level={level} streak={streak}
          onStartCard={handleStartCard}
          onOpenDailyStory={handleOpenDailyStory}
          onStartTrack={(domain, track, articleIndex) => { handleStartTrack(domain, track, articleIndex); }}
          onBrowseTracks={() => setActiveScreen("explore")}
          onSearch={() => { setActiveScreen("create"); setPhase("idle"); setResult(null); }}
        />
      )}

      {activeScreen === "explore" && (
        <ExploreScreen
          t={t} lang={lang} trackProgress={trackProgress}
          onStartTrack={(domain, track, articleIndex) => { handleStartTrack(domain, track, articleIndex); setActiveScreen("create"); }}
        />
      )}

      {activeScreen === "game" && (
        <GameScreen
          t={t} lang={lang} user={user}
          trackProgress={trackProgress}
          xp={xp} level={level} streak={streak}
          weeklyGoal={weeklyGoal}
          onSaveWeeklyGoal={saveWeeklyGoal}
          onStartTrack={() => setActiveScreen("explore")}
          onContinueTrack={(domain, track, articleIndex) => {
            handleStartTrack(domain, track, articleIndex);
            setActiveScreen("create");
          }}
          onOpenHistoryArticle={(domain, track, articleIndex) => {
            handleStartTrack(domain, track, articleIndex, activePreset || 'popscience');
            setActiveScreen("create");
          }}
          onOpenDailyStory={handleOpenDailyStory}
        />
      )}

      {activeScreen === "create" && (
        <CreateScreen
          t={t} lang={lang}
          input={input} setInput={setInput}
          length={length} setLength={setLength}
          tone={tone} setTone={setTone}
          style={style} setStyle={setStyle}
          activePreset={activePreset} setActivePreset={setActivePreset}
          styleOpen={styleOpen} setStyleOpen={setStyleOpen}
          phase={phase} result={result} facts={facts} storyReady={storyReady}
          errorMsg={errorMsg} copied={copied} reaction={reaction}
          activeTrack={activeTrack} setActiveTrack={setActiveTrack}
          onRun={run} onRead={() => setPhase("done")}
          onPick={(article) => { setInput(article.title); run(article.title, true); }}
          onBack={() => { setPhase("idle"); setDisambigResults([]); setStyleOpen(true); }}
          onRegen={handleRegen}
          onReaction={handleReaction}
          onShare={handleShare}
          onCopy={handleCopy}
          onNextInTrack={handleNextInTrack}
          onJumpToTrack={handleJumpToTrack}
          disambigResults={disambigResults} disambigQuery={disambigQuery}
          user={user} authEmail={authEmail} setAuthEmail={setAuthEmail}
          magicSent={magicSent} onMagicLink={signInWithMagicLink} onGoogle={signInWithGoogle} onSignOut={signOut}
          showFeedback={showFeedback} setShowFeedback={setShowFeedback}
        />
      )}

{/* Reading cycle overlays: reading → flashcards → victory (fullscreen) */}
      {activeScreen === "create" && phase === "done" && result && (() => {
        const pages = paginateStory(result.story, result.images || [], result.pullQuote || null, result.sceneImages || []);
        const presetIdx = activePreset ? PRESETS.findIndex(p => p.id === activePreset) : -1;
        return (
          <ReadingView
            lang={lang} result={result} activeTrack={activeTrack}
            pages={pages} pageIndex={pageIndex} setPageIndex={setPageIndex}
            onFinish={goToFlashcards}
            onExit={exitReading}
            presetLabel={presetIdx >= 0 ? t.presets[presetIdx] : null}
            onChangeStyle={() => setStyleSwitcherOpen(true)}
          />
        );
      })()}

      {activeScreen === "create" && phase === "flashcards" && (
        <FlashcardsView
          lang={lang} cards={flashcards} index={fcIndex}
          revealed={fcRevealed} setRevealed={setFcRevealed}
          loading={fcLoading} onAnswer={answerFlashcard} result={result}
          onExit={exitReading}
          onShareCard={handleShareFlashcard}
          onBack={backFlashcard}
        />
      )}

      {activeScreen === "create" && phase === "victory" && (
        <VictoryView
          lang={lang} result={result} fcResults={fcResults}
          xp={{ gained: 10 }} streak={streak} activeTrack={activeTrack}
          onNext={finishVictory}
        />
      )}

      {showFeedback && (
        <FeedbackModal
          result={result} lang={lang}
          lengthHint={t.lengthHints[length-1]} toneHint={t.toneLabels[tone-1]} styleHint={t.styleLabels[style-1]}
          onClose={()=>setShowFeedback(false)}
        />
      )}

      {styleGate && (
        <StyleGateModal
          t={t} lang={lang} mode="start"
          domain={styleGate.domain} track={styleGate.track}
          currentPreset={activePreset}
          onConfirm={handleStyleGateConfirm}
          onClose={() => setStyleGate(null)}
        />
      )}

      {styleSwitcherOpen && (
        <StyleGateModal
          t={t} lang={lang} mode="switch"
          currentPreset={activePreset}
          onConfirm={handleStyleSwitch}
          onClose={() => setStyleSwitcherOpen(false)}
        />
      )}

      {!showOnboarding && (
        <div style={{
          position:"fixed", bottom:0, left:0, right:0,
          background:D.bg, borderTop:`1px solid ${D.borderSoft}`,
          display:"flex", zIndex:90,
          paddingBottom:"env(safe-area-inset-bottom)",
          fontFamily:D.ui,
        }}>
          {[
            { screen:"home",     icon:"⚡", label:lang==="ru"?"Сегодня":"Today" },
            { screen:"explore",  icon:"🗺", label:lang==="ru"?"Карта":"Explore" },
            { screen:"create",   icon:"🔍", label:lang==="ru"?"Поиск":"Search" },
            { screen:"game",     icon:"◉", label:lang==="ru"?"Прогресс":"Progress" },
          ].map(tab => {
            const active = activeScreen === tab.screen;
            return (
              <button key={tab.screen}
                onClick={() => {
                  if (tab.screen === "game") {
                    setActiveScreen("game");
                  } else if (tab.screen === "create") {
                    setActiveScreen("create");
                    setPhase("idle");
                    setResult(null);
                    setActiveTrack(null);
                  } else {
                    setActiveScreen(tab.screen);
                  }
                }}
                style={{
                  flex:1, padding:"10px 0 12px",
                  display:"flex", flexDirection:"column", alignItems:"center", gap:"3px",
                  background:"transparent", border:"none", cursor:"pointer",
                  color: active ? D.ember : D.ter2,
                  transition:"color .15s",
                  minHeight:"56px",
                }}
              >
                <span style={{fontSize:"20px", lineHeight:1}}>{tab.icon}</span>
                <span style={{fontSize:"10px", letterSpacing:"0.02em", fontWeight: active ? 600 : 500}}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {hookStory && (
        <HookScreen
          lang={lang} story={hookStory} card={hookCard}
          loading={hookLoading} answered={hookAnswered}
          onAnswer={handleHookAnswer}
          onReadFull={handleHookReadFull}
          onAnother={handleHookAnother}
          onClose={handleHookClose}
        />
      )}

      {showEmailCapture && (
        <EmailCapturePrompt
          lang={lang} user={user}
          authEmail={authEmail} setAuthEmail={setAuthEmail}
          onMagicLink={signInWithMagicLink} onGoogle={signInWithGoogle}
          magicSent={magicSent}
          onSkip={handleEmailCaptureSkip}
          onAlreadyAuthSubscribe={handleAlreadyAuthSubscribe}
        />
      )}

      {showInstallPrompt && (
        <InstallPrompt
          lang={lang}
          onAccept={handleInstallPromptAccept}
          onDismiss={handleInstallPromptDismiss}
        />
      )}

      {showAuth && (
        <div onClick={e=>e.target===e.currentTarget&&setShowAuth(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:1000}}>
          <div style={{background:D.panel,borderRadius:"20px 20px 0 0",padding:"24px 20px 32px",width:"100%",maxWidth:"480px",animation:"wt_slideup .35s ease",border:`1px solid ${D.border}`,borderBottom:"none"}}>
            <p style={{fontFamily:D.serif,fontSize:"18px",color:D.h,margin:"0 0 4px"}}>
              {lang==="ru"?"Войти в WikiTale":lang==="de"?"Anmelden":"Sign in to WikiTale"}
            </p>
            <p style={{fontSize:"12px",color:D.sec,margin:"0 0 20px",fontFamily:D.ui}}>
              {lang==="ru"?"Сохраним прогресс и предпочтения":lang==="de"?"Fortschritt und Einstellungen speichern":"Save your progress and preferences"}
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:"8px",marginBottom:"12px"}}>
              <button onClick={signInWithGoogle} style={{width:"100%",padding:"11px",background:D.panel2,border:`1px solid ${D.border}`,borderRadius:"12px",fontFamily:D.ui,fontSize:"13px",cursor:"pointer",color:D.h}}>
                G · {lang==="ru"?"Войти через Google":lang==="de"?"Mit Google":"Continue with Google"}
              </button>
              <div style={{display:"flex",gap:"8px"}}>
                <input type="email" placeholder="your@email.com" value={authEmail} onChange={e=>setAuthEmail(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&signInWithMagicLink()}
                  style={{flex:1,background:D.panel3,border:`1px solid ${D.border}`,borderRadius:"10px",padding:"10px 14px",fontSize:"13px",color:D.h,fontFamily:D.ui,outline:"none"}}/>
                <button onClick={signInWithMagicLink} style={{background:D.ember,color:"#fff",border:"none",borderRadius:"10px",padding:"10px 18px",fontSize:"13px",cursor:"pointer",fontFamily:D.serif}}>→</button>
              </div>
              {magicSent && (
                <p style={{fontSize:"12px",color:D.emberBright,fontFamily:D.ui,textAlign:"center",margin:0}}>
                  {lang==="ru"?"✉️ Проверь почту":"✉️ Check your inbox"}
                </p>
              )}
            </div>
            <button onClick={()=>setShowAuth(false)} style={{width:"100%",padding:"8px",background:"transparent",border:"none",color:D.ter,fontSize:"12px",cursor:"pointer",fontFamily:D.ui}}>
              {lang==="ru"?"Закрыть":lang==="de"?"Schließen":"Close"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}